/**
 * Full-screen states: start, pause, game over.
 *
 * All three are translucent — the table stays visible and lit behind them,
 * pushed back with a blur rather than covered by a panel.
 *
 * The start screen has one job beyond looking expensive: teach the four ways a
 * pile resolves in a single glance, using little card stacks instead of a
 * paragraph. The game-over screen has one job too: pay the round off. The
 * breakdown is revealed as a sequence — base, perfect game, no bust, time —
 * each counting up into a running total that lands on the final number.
 */

import { arrivalStore, currentPillCount } from '@/chest/arrival-state';
import { CHESTS_ENABLED } from '@/core/flags';
import { burst, chestIcon, lockIcon } from '@/chest/glyphs';
import { openSignIn, openSignup, viewerStore } from '@/chest/signup-state';
import { chestStore, openNextChest } from '@/chest/chests-state';
import { gsap } from 'gsap';
import { rankPalette } from '@/core/tokens';
import {
  RANK_NAMES,
  RANK_SPAN,
  rankFor,
  tierNumeral,
} from '@/game/xp';
import type {
  ChallengeCard,
  ChallengeCounts,
  ChallengeRecord,
  ChallengeView,
  Finance,
  GameRow,
  GamesPage,
  OpenChallenge,
  Profile,
  QueueTier,
  RankView,
  Result,
  TopStreaks,
} from '@/net/api';
import { orientation } from '@/pixi/layout';
import { mountRewards, type RewardsApi } from '@/ui/rewards';
import { cosmetic as cosmeticOf } from '@/game/chests';
import { captionChunks, gameRowData, matchRowData, matchStatus, publicRows, rightSide, rowLabel, shortAgo, timeAgo, type GameRowData } from '@/ui/game-row';
import { profileTiles } from '@/ui/profile-stats';
import { openStreakSheet } from '@/ui/streak-sheet';
import { PanelFit } from '@/ui/fit';
import { type Balance, canAfford, isShort, settleFee, ticketAskedAt } from '@/ui/afford';
import { chipAriaLabel, createChipHint, customChipHint, customYours, quickChipAriaLabel, takeChipHint } from '@/ui/chip-hint';
import { openLinkLabel, openOwnWagers } from '@/ui/own-wagers-sheet';
import { kitSwitch } from '@/ui/settings';
import { dockTapAction, type DockTarget } from '@/ui/dock-nav';
import { DEFAULT_BOUNDS, FIRST_FEE, customCount, feeProblem, fmtAmount, openRows, parseFeeText, type FeeBounds } from '@/ui/fee';
import { PushPrompt } from '@/ui/push-prompt';
import { CHALLENGE_ANCHOR, shareIsPrimary, shareable } from '@/ui/challenge-me';
import { faucetEntry, type FaucetEntry } from '@/ui/faucet-entry';
import { el, formatScore, prefersReducedMotion } from '@/ui/hud';

/** Tones are named palette entries; the CSS resolves them from the tokens. */
export type Tone = 'gold' | 'mint' | 'violet' | 'rose' | 'white' | 'azure';

function setTone(node: HTMLElement, tone: Tone): void {
  node.style.setProperty('--w21-tone', `var(--w21-${tone})`);
  node.style.setProperty('--w21-tone-rgb', `var(--w21-${tone}-rgb)`);
}

/**
 * Shared shell: a full-bleed scrim with a centred, edgeless panel.
 *
 * Every screen is fitted to the viewport from here, rather than each one
 * remembering to do it. The start screen was fitted and the other two were not,
 * which is why the game-over breakdown ran off the bottom of a short window
 * while the start screen was fine on the same machine — the same mistake as
 * fixing a layout rule at one breakpoint. A screen that cannot be seen is
 * broken whichever screen it is, so this is not something a screen opts into.
 *
 * The fit runs whether or not the screen is showing. An inactive screen is
 * still laid out — it is hidden with `visibility`, not `display` — so it
 * measures correctly, and `ResizeObserver` only costs anything when something
 * actually changes size.
 */
export function createScreen(
  modifier: string,
  scrolls = false,
  tag: 'div' | 'main' = 'div',
): {
  screen: HTMLElement;
  panel: HTMLElement;
  fit: PanelFit;
} {
  const screen = el('div', `w21-screen w21-screen--${modifier}`);
  screen.dataset.active = 'false';
  screen.setAttribute('role', 'dialog');
  screen.setAttribute('aria-modal', 'true');
  const panel = el(tag, `w21-screen-panel w21-screen-panel--${modifier}`);
  screen.append(panel);

  const fit = new PanelFit(screen, panel, scrolls);
  fit.start();
  return { screen, panel, fit };
}

/** Focus without fighting the visibility transition. */
function focusSoon(node: HTMLElement): void {
  requestAnimationFrame(() => {
    if (node.isConnected) node.focus({ preventScroll: true });
  });
}

/* ================================================================ start == */

/**
 * The $CHAIN mark — the Chain Games emblem, as a coin.
 *
 * **This is the one thing that is pixel-identical across every Chain Games
 * title.** It is the token, not the game, so the gradients are taken verbatim
 * from the shared implementation rather than restyled to this palette. The only
 * concession is the inner disc, which is the emblem's own dark ground.
 *
 * A rim disc in a warm gradient, a dark inner disc, and the mark on top in the
 * face gradient. Inline rather than an asset because it appears at three sizes
 * — 28px in the balance pill, 40px in a pot card, 20px inline in a chip — and a
 * raster at three sizes is three chances for one of them to be wrong.
 *
 * The gradient ids are suffixed per instance: two coins on one page with the
 * same `id` means the second silently borrows the first one's fill, and a page
 * with a pill and a pot card has exactly that.
 */
let coinSeq = 0;

export function coin(): HTMLElement {
  const mark = el('span', 'w21-coin');
  mark.setAttribute('aria-hidden', 'true');
  mark.innerHTML = coinSvg();
  return mark;
}

/** The coin's markup on its own: the share card rasterises it for the canvas. */
export function coinSvg(): string {
  const n = ++coinSeq;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4.5 0 337 337">`
    + `<defs>`
    + `<linearGradient id="w21-coin-face-${n}" x1="0" y1="0" x2="1" y2="1">`
    + `<stop offset="0" stop-color="#ffd88f"/>`
    + `<stop offset=".55" stop-color="#ffb84d"/>`
    + `<stop offset="1" stop-color="#d8861a"/>`
    + `</linearGradient>`
    + `<linearGradient id="w21-coin-rim-${n}" x1="0" y1="0" x2="0" y2="1">`
    + `<stop offset="0" stop-color="#fff1cf"/>`
    + `<stop offset="1" stop-color="#b86f10"/>`
    + `</linearGradient>`
    + `</defs>`
    + `<circle cx="164" cy="168.5" r="168" fill="url(#w21-coin-rim-${n})"/>`
    + `<circle cx="164" cy="168.5" r="154" fill="#2a1600"/>`
    + `<path fill="url(#w21-coin-face-${n})" d="M164,4.5C73.4,4.5,0,77.9,0,168.5s73.4,164,164,164`
    + `s164-73.4,164-164S254.6,4.5,164,4.5z M156,247.7 l-92.9-76.9l92.2-115.4v73.4l-16.9-13.2l-33.9,`
    + `39.9l51.5,41.1V247.7z M172.5,281.6v-73.5l17.1,13.2l33.9-39.8l-51.7-41.1v-51 l93.1,76.8L172.5,`
    + `281.6z"/>`
    + `</svg>`
  );
}


/**
 * A stable hue per username.
 *
 * Java-style string hash, so the same player is the same colour on every device
 * and two players are almost never the same. Shared with the other titles: an
 * avatar that changes colour between games is a different person as far as
 * anybody glancing at it is concerned.
 */
export function hueOf(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (Math.imul(h, 31) + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

/**
 * The two letters on the disc: the first two LETTERS of the name without its
 * `guest_` prefix — "DA" for guest_78d4af56, not "78". Digits only when the
 * name has no letters to give.
 */
export function initialsOf(name: string): string {
  const part = name.replace(/^guest_/i, '');
  const letters = part.replace(/[^a-z]/gi, '');
  return (letters.length >= 2 ? letters : part).slice(0, 2).toUpperCase();
}

/**
 * What a guest is told about the account they are holding, wherever they see their balance or progress.
 *
 * A guest has no password: the browser's cookie is the only way back in, so clearing site data, signing out or
 * switching browser loses the account (and its $CHAIN, rank and chests) for good. Said plainly, with the one action
 * that changes it.
 */
export function guestNote(onCreateAccount: () => void): HTMLElement {
  const note = el('div', 'w21-note w21-warm w21-guest-note');
  note.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v6M12 7v.5"></path></svg>';
  const text = el('span');
  const link = el('a', undefined, 'Create an account') as HTMLAnchorElement;
  link.href = '#auth';
  link.style.color = 'inherit';
  link.addEventListener('click', (event) => {
    event.preventDefault();
    onCreateAccount();
  });
  text.append(
    document.createTextNode('This guest lives in this browser only. Sign out or clear your data and it is gone for good. '),
    link,
    document.createTextNode(' to keep your $CHAIN and rank.'),
  );
  note.append(text);
  return note;
}

export interface StartScreenCallbacks {
  /**
   * The shell bar's targets, wired once in `mountUI` and handed to every
   * screen, so the bar is the same bar on every route. Without this each
   * screen built its own from its own callbacks, and the History screen's had
   * nothing on it: balance and avatar no-ops, brand hidden, the only way out a
   * Back button under thirty cards.
   */
  bar?: TopBarCallbacks;
  /** Solo: a fresh random deck, nobody to beat. */
  onStart(): void;
  /** Play the walkthrough again, on demand. */
  onTutorial(): void;
  /** The full How-it-works reference. */
  onGuide?(): void;
  /** Take the next deck out of the shared pool and chase whoever set it. */
  onAsync(): void;
  /** Open a lobby and get a link to send someone. */
  onLobby(): void;
  /** Open the sign-up sheet (the guest note's link). */
  onCreateAccount?(): void;
  /** End the session and go back to being nobody. */
  onSignOut(): void;
  /** Every challenge this player has a seat in. */
  onHistory(): void;
  /** This player's own profile. */
  onProfile(): void;
  /** One challenge in full — a recent card on the home page. */
  onOpenChallenge?: (id: string) => void;
  /** The home card's way to the leaderboard. */
  onLeaderboard?(): void;
  /** The account box's "Free tokens" (the weekly faucet). Absent while the faucet is off: no button. */
  onFaucet?(): void;
}

/**
 * History: every challenge this player has a seat in.
 *
 * A list rather than a table, because the interesting unit is a match and a
 * match has an outcome — reading a row should answer "did I win" before it
 * answers anything else, which a grid of numbers does not.
 */
export class HistoryScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;

  readonly #column: HTMLElement;
  readonly #head: HTMLElement;
  readonly #fit: PanelFit;

  constructor(
    private readonly callbacks: { onBack(): void; onOpen(id: string): void; bar?: TopBarCallbacks },
  ) {
    const { screen, panel, fit } = createScreen('history', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', '21 Wild — your challenges');

    // The same bar as every other screen — brand, nav, balance, avatar, all
    // live. This used to build one with every target a no-op and the brand
    // hidden, which left a player no way off the page but the Back button
    // under the whole list.
    this.topBar = new TopBar(
      callbacks.bar ?? { onHistory: () => undefined, onHome: () => callbacks.onBack() },
    );
    screen.append(this.topBar.root);

    // The kit's page: no card around the list, no inner scroller, no Back
    // pill — the page scrolls and the bar is the way out. The panel used to be
    // a 730px glass card with its own fading scroll region and a primary
    // button under thirty cards, which is three pieces of furniture Blockari
    // does not have and the critic counts every one of.
    panel.className = 'w21-page';
    this.#column = el('div', 'w21-column w21-wide');
    this.#head = el('div', 'w21-row w21-between');
    this.#head.append(el('h2', undefined, 'Your challenges'));
    this.#column.append(this.#head);
    panel.append(this.#column);
  }

  /** The W–L tally beside the heading: the server's record when it sent one, else a tally of the rows in hand. */
  #paintRecord(rows: ChallengeCard[], record?: ChallengeRecord | null): void {
    // The server's record when it sent one, and only otherwise a tally of the
    // rows in hand. The list is the last 30, so counting it produced a header
    // that disagreed with the same player's profile — "15 won · 11 lost" over
    // a record of 50 from 85.
    const local = rows.filter((row) => row.outcome !== null);
    const played = record ? record.played : local.length;
    const won = record ? record.won : local.filter((row) => row.outcome === 'won').length;
    const drawn = record ? record.drawn : local.filter((row) => row.outcome === 'draw').length;
    // A tie is not a loss. The bar is won over everything finished, so a
    // player who tied half their games sees the rate they actually have.
    this.#head.querySelector('.w21-record')?.remove();
    const tally = recordOf(won, played - won);
    if (tally) this.#head.append(tally);
    if (drawn > 0) tally?.setAttribute('title', `${Math.round((won / played) * 100)}% win rate · ${drawn} tied`);
  }

  show(rows: ChallengeCard[], record?: ChallengeRecord | null): void {
    this.#paintRecord(rows, record);
    this.#column.querySelector('.w21-glist, .w21-panel')?.remove();
    if (rows.length === 0) {
      // Nothing to list: the kit's Empty in a panel, with the two ways to
      // change that. The bar's own Create/Take targets, so the buttons go
      // where the nav goes.
      const panel = el('div', 'w21-panel');
      const empty = emptyBlock(
        'No challenges yet',
        'Create a challenge and share the code, or take the oldest open one at your stake.',
      );
      const actions = el('div', 'w21-row');
      const { onCreate, onTake } = this.callbacks.bar ?? {};
      if (onCreate) actions.append(kitButton('Create one', 'w21-btn w21-warm', onCreate));
      if (onTake) actions.append(kitButton('Take one', 'w21-btn', onTake));
      if (actions.childElementCount) empty.append(actions);
      panel.append(empty);
      this.#column.append(panel);
    } else {
      this.#column.append(matchRows(rows, (id) => this.callbacks.onOpen(id)));
    }
    this.#fit.measure();
  }

  /**
   * The list again, from a newer fetch, while the screen is up: no empty or loading state in between, the scroll
   * where it was, and only the rows that differ touched (a settled row is swapped for its new self without the
   * entrance animation, the rest are left alone, focus and hover included).
   */
  refresh(rows: ChallengeCard[], record?: ChallengeRecord | null): void {
    const top = this.root.scrollTop;
    const list = this.#column.querySelector<HTMLElement>('.w21-glist');
    if (!list || rows.length === 0) {
      this.show(rows, record);
    } else {
      this.#paintRecord(rows, record);
      patchRows(list, matchRows(rows, (id) => this.callbacks.onOpen(id)));
      this.#fit.measure();
    }
    this.root.scrollTop = top;
  }
}

/**
 * Bring `current` (a list of game rows on screen) to `next` (the same list, built fresh) by changing only what
 * differs. Rows are matched by their challenge link, not by position, because a settled challenge moves in the list:
 * a row that is the same is left alone (focus and hover included), one that changed is replaced without the entrance
 * animation, new ones are added, gone ones removed, and the order is made to match.
 */
export function patchRows(current: HTMLElement, next: HTMLElement): void {
  // Equal means the same markup apart from its entrance-stagger slot and the no-entrance class this adds.
  const key = (row: Element): string => row.outerHTML.replace(' w21-still', '').replace(/ style="--k: \d+;"/, '');
  const id = (row: Element, at: number): string => row.getAttribute('href') ?? `#${at}`;
  const active = document.activeElement instanceof HTMLAnchorElement && current.contains(document.activeElement)
    ? document.activeElement.getAttribute('href')
    : null;
  const have = new Map<string, Element>();
  Array.from(current.children).forEach((row, i) => have.set(id(row, i), row));
  let cursor: Element | null = current.firstElementChild;
  Array.from(next.children).forEach((row, i) => {
    const old = have.get(id(row, i));
    let placed: Element = row;
    if (old && key(old) === key(row)) {
      placed = old;
      have.delete(id(row, i));
    } else {
      row.classList.add('w21-still');
      // The old row for this challenge is replaced where the new one lands, not left to be removed below.
      if (old) have.delete(id(row, i));
      if (old === cursor) cursor = old?.nextElementSibling ?? null;
      old?.remove();
    }
    if (placed === cursor) cursor = cursor.nextElementSibling;
    else current.insertBefore(placed, cursor);
  });
  for (const gone of have.values()) gone.remove();
  if (active !== null && document.activeElement === document.body) {
    current.querySelector<HTMLElement>(`a[href="${CSS.escape(active)}"]`)?.focus({ preventScroll: true });
  }
}

/* ------------------------------------------------------- the match card -- */

/**
 * The rank cube's geometry, in a 64-unit box — Blockari's `CUBE`, verbatim.
 *
 * Shared across the titles so that a rank badge is the same object on every
 * Chain Games page: the same three faces, the same rim, the same five pips
 * under it. Only the colours are ours (`--w21-rank-N`). The compact badge —
 * anything under `PIPS_FROM` — crops the box to the cube (`10 2 44 48`) and
 * drops the pips, which at 18px would be smaller than the gaps between them.
 */
export const CUBE = {
  top: '32,4 52,15 32,26 12,15',
  left: '12,15 32,26 32,48 12,37',
  right: '32,26 52,15 52,37 32,48',
  hull: '32,4 52,15 52,37 32,48 12,37 12,15',
  rim: 'M12,15 L32,4 L52,15',
  spec: { cx: 26, cy: 12, rx: 5, ry: 2.4, rot: -28 },
  pipsY: 57,
  pipsX: [16, 24, 32, 40, 48],
  pipHalf: 3.2,
} as const;

/** Pips up to this tier; past it the badge writes the number instead. */
export const PIP_MAX = 5;
/** Badges this size and up are `lit` and carry the tier pips. */
export const PIPS_FROM = 56;

/** `rank-2`: the kit's rank class, clamped onto the ladder. Unranked → 0. */
export function rankClass(index: number): string {
  return `w21-rank-${Math.max(0, Math.min(RANK_NAMES.length - 1, index))}`;
}

/**
 * The rank badge in the kit's markup — `span.rank-badge > svg` with the CUBE
 * geometry — as opposed to `rankBadge()` above, which is the older 30×32 gem
 * and still carries the take rows and the results tray until those screens
 * are ported. One rank, one cube, so a player crossing between the titles is
 * reading the same emblem.
 */
export function rankBadgeCube(
  rank: { index: number; name: string; tier: number },
  size = 24,
): HTMLElement {
  const compact = size < PIPS_FROM;
  const legend = rank.index === RANK_NAMES.length - 1;
  const label = rank.index === UNRANKED ? 'No rank on record' : `${rank.name} ${tierNumeral(rank.tier)}`;
  const badge = el(
    'span',
    `w21-rank-badge ${rankClass(rank.index)} ${compact ? 'w21-compact' : 'w21-lit'}${legend ? ' w21-legend' : ''}`,
  );
  if (rank.index === UNRANKED) {
    // No rank on record: slate, with the saturation actually at zero. A
    // dimmed hue still reads as a rank, which is the one thing this badge
    // exists not to say.
    badge.style.setProperty('--rank', '#8e9099');
    badge.style.setProperty('--rank-rgb', '142 144 153');
  }
  badge.style.setProperty('--size', `${size}px`);
  badge.title = label;
  badge.setAttribute('role', 'img');
  badge.setAttribute('aria-label', label);

  const s = CUBE.spec;
  const parts = [
    `<svg viewBox="${compact ? '10 2 44 48' : '0 0 64 64'}" aria-hidden="true">`,
    `<polygon class="w21-f-left" points="${CUBE.left}"></polygon>`,
    `<polygon class="w21-f-right" points="${CUBE.right}"></polygon>`,
    `<polygon class="w21-f-top" points="${CUBE.top}"></polygon>`,
    `<ellipse class="w21-spec" cx="${s.cx}" cy="${s.cy}" rx="${s.rx}" ry="${s.ry}" transform="rotate(${s.rot} ${s.cx} ${s.cy})"></ellipse>`,
    `<path class="w21-rim" d="${CUBE.rim}"></path>`,
    `<polygon class="w21-outline" points="${CUBE.hull}"></polygon>`,
  ];
  if (!compact) {
    if (rank.tier > PIP_MAX) {
      parts.push(
        `<text class="w21-tier-num" x="32" y="${CUBE.pipsY + 5}" text-anchor="middle">${tierNumeral(rank.tier)}</text>`,
      );
    } else {
      const h = CUBE.pipHalf;
      const y = CUBE.pipsY;
      CUBE.pipsX.forEach((x, i) => {
        parts.push(
          `<polygon class="w21-pip${i < rank.tier ? ' w21-lit' : ''}" points="${x},${y - h} ${x + h},${y} ${x},${y + h} ${x - h},${y}"></polygon>`,
        );
      });
    }
  }
  parts.push('</svg>');
  badge.innerHTML = parts.join('');
  return badge;
}

/**
 * The rank badge as the rank's CARD.
 *
 * 21 Wild's ranks are named after the deck, so the badge is the card itself —
 * a 2, a 7, a ten, the court cards, the ace, the hand (ace and jack) and the
 * wild jack — drawn in the same 64-unit box as Blockari's cube, at the same
 * sizes, with the same five pips under it. Adam's call, relayed through
 * Blockari: the cube is Blockari's identity and this is ours, so the card
 * shows everywhere a rank does.
 *
 * The geometry is deliberately the cube's box (`0 0 64 64`, pips at `pipsY`)
 * so every screen that was measured against the kit keeps its layout: only
 * what is drawn inside the box changed.
 */
export const RANK_CARD = {
  /** The card's face, before the tilt. */
  x: 19,
  y: 5,
  w: 26,
  h: 37,
  r: 3.4,
  tilt: -7,
  /** Blackjack's second card, behind and tipped the other way. */
  pairTilt: 13,
  pairShift: 4.6,
} as const;

/** The pip a rank's card carries, and the letter in its corner. */
export const RANK_GLYPHS: readonly string[] = ['2', '7', '10', 'J', 'Q', 'K', 'A', 'A', 'J'];

/** A spade, in the badge's box: the suit every rank card is printed in. */
export const SPADE_PATH =
  'M0,-7.4 C2.6,-4.2 6.6,-2.2 6.6,1.1 C6.6,3.4 4.9,4.9 2.9,4.9 C1.7,4.9 0.7,4.3 0,3.4 ' +
  'C0.4,5.6 1.1,6.6 2.1,7.4 L-2.1,7.4 C-1.1,6.6 -0.4,5.6 0,3.4 C-0.7,4.3 -1.7,4.9 -2.9,4.9 ' +
  'C-4.9,4.9 -6.6,3.4 -6.6,1.1 C-6.6,-2.2 -2.6,-4.2 0,-7.4 Z';

function cardFaceSvg(
  glyph: string,
  tilt: number,
  shift: number,
  klass: string,
  scale = 1,
  band = false,
): string {
  const C = RANK_CARD;
  const w = C.w * scale;
  const h = C.h * scale;
  const x = 32 - w / 2 + shift;
  const y = 26 - h / 2;
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    `<g class="${klass}" transform="rotate(${tilt} 32 26)">` +
    `<rect class="w21-card-face" x="${x}" y="${y}" width="${w}" height="${h}" rx="${C.r * scale}"></rect>` +
    `<rect class="w21-card-edge" x="${x}" y="${y}" width="${w}" height="${h}" rx="${C.r * scale}"></rect>` +
    `<text class="w21-card-glyph" x="${cx}" y="${cy + 1.5}" text-anchor="middle" dominant-baseline="middle">${glyph}</text>` +
    `<path class="w21-card-pip" d="${SPADE_PATH}" transform="translate(${cx + w * 0.3} ${cy + h * 0.3}) scale(${0.52 * scale})"></path>` +
    // Wild's card is the jack of spades — the same card the Jack rank wears —
    // so it carries the printed wild band, exactly as the deck does, and the
    // two badges can never be mistaken for each other.
    (band
      ? `<rect class="w21-card-band" x="${x}" y="${cy - h * 0.1}" width="${w}" height="${h * 0.2}"></rect>`
      : '') +
    `</g>`
  );
}

export function rankBadgeCard(
  rank: { index: number; name: string; tier: number },
  size = 24,
): HTMLElement {
  const compact = size < PIPS_FROM;
  const legend = rank.index === RANK_NAMES.length - 1;
  const blackjack = rank.index === 7;
  const label = rank.index === UNRANKED ? 'No rank on record' : `${rank.name} ${tierNumeral(rank.tier)}`;
  const badge = el(
    'span',
    `w21-rank-badge w21-rank-card ${rankClass(rank.index)} ${compact ? 'w21-compact' : 'w21-lit'}${legend ? ' w21-legend' : ''}`,
  );
  if (rank.index === UNRANKED) {
    // No rank on record: slate, with the saturation actually at zero. A dimmed
    // hue still reads as a rank, which is the one thing this badge exists not
    // to say.
    badge.style.setProperty('--rank', '#8e9099');
    badge.style.setProperty('--rank-rgb', '142 144 153');
  }
  badge.style.setProperty('--size', `${size}px`);
  badge.title = label;
  badge.setAttribute('role', 'img');
  badge.setAttribute('aria-label', label);

  const glyph = rank.index === UNRANKED ? '?' : (RANK_GLYPHS[rank.index] ?? '2');
  const parts = [`<svg viewBox="${compact ? '11 1 42 48' : '0 0 64 64'}" aria-hidden="true">`];
  // Blackjack is a hand, not a card: the ace behind, the jack in front.
  if (blackjack) parts.push(cardFaceSvg('J', RANK_CARD.pairTilt, RANK_CARD.pairShift, 'w21-card-back', 0.94));
  parts.push(cardFaceSvg(glyph, RANK_CARD.tilt, blackjack ? -2.4 : 0, 'w21-card-front', 1, legend));
  if (!compact) {
    if (rank.tier > PIP_MAX) {
      parts.push(
        `<text class="w21-tier-num" x="32" y="${CUBE.pipsY + 5}" text-anchor="middle">${tierNumeral(rank.tier)}</text>`,
      );
    } else {
      const h = CUBE.pipHalf;
      const y = CUBE.pipsY;
      CUBE.pipsX.forEach((x, i) => {
        parts.push(
          `<polygon class="w21-pip${i < rank.tier ? ' w21-lit' : ''}" points="${x},${y - h} ${x + h},${y} ${x},${y + h} ${x - h},${y}"></polygon>`,
        );
      });
    }
  }
  parts.push('</svg>');
  badge.innerHTML = parts.join('');
  return badge;
}

/**
 * The kit's `PlayerTag`: badge and name as one unit, the badge AFTER the name
 * when `mirror` — the right-hand side of a VS row reads outward.
 */
function kitPlayerTag(
  rank: { index: number; name: string; tier: number },
  name: string,
  options: { mirror?: boolean; to?: { href: string; onOpen: () => void } } = {},
): HTMLElement {
  const tag = el(options.to ? 'a' : 'span', `w21-player-tag w21-sm ${rankClass(rank.index)}`);
  if (options.to) {
    const { href, onOpen } = options.to;
    (tag as HTMLAnchorElement).href = href;
    tag.addEventListener('click', (event) => {
      event.preventDefault();
      onOpen();
    });
  }
  const badge = rankBadgeCard(rank, 18);
  const label = el('span', 'w21-pt-name', name);
  if (options.mirror) tag.append(label, badge);
  else tag.append(badge, label);
  return tag;
}

/** `Match LV 8` — the in-round level, in the indigo chip the kit gives it. */
function matchLevelChip(level: number): HTMLElement {
  const chip = el('span', 'w21-chip w21-indigo', `Match LV ${level}`);
  chip.title = `In-match level ${level} reached`;
  return chip;
}

/**
 * The home screen's recent card: head (fee, Created/Taken chip), the status pill and a caption. The compact form of
 * a challenge, three of them above "All challenges"; History, a profile's recent list and Recent games use the
 * one-glance `gameRow` instead (Blockari's `GameRow`).
 *
 * `a.match` with `.head`, the `.status` chip as a SIBLING grid cell (the card is `1fr auto`), then the `.when`
 * caption. `k` staggers the entrance.
 */
export function matchCard(row: ChallengeCard, onOpen?: (id: string) => void, k = 0): HTMLElement {
  const status = matchStatus(row);
  const card = el('a', 'w21-match') as HTMLAnchorElement;
  card.href = `#challenge/${row.id}`;
  card.style.setProperty('--k', String(k));
  // Kept for the capture tools, which pick a card by its outcome.
  card.dataset.outcome = row.outcome ?? (row.myScore === null ? 'yours' : 'open');
  card.addEventListener('click', (event) => {
    event.preventDefault();
    onOpen?.(row.id);
  });

  const head = el('span', 'w21-head');
  head.append(el('span', 'w21-fee', `${formatScore(row.entryFee)} $CHAIN`), el('span', 'w21-chip', row.role === 'creator' ? 'Created' : 'Taken'));
  if (row.isPrivate) head.append(el('span', 'w21-chip w21-indigo', 'Private'));
  card.append(head, el('span', `w21-status${status.cls ? ` w21-${status.cls}` : ''}`, status.text));

  const vs = row.opponent ? ` · vs ${row.opponent.name}` : '';
  card.append(el('span', 'w21-when', `${timeAgo(row.createdAt)}${vs}`));
  return card;
}

/** The home screen's rail of recent cards (`.timeline`, each staggered by its index). */
export function timeline(rows: readonly ChallengeCard[], onOpen?: (id: string) => void): HTMLElement {
  const list = el('div', 'w21-timeline');
  rows.forEach((row, k) => list.append(matchCard(row, onOpen, k)));
  return list;
}

/* ------------------------------------------------------- the game row -- */

/**
 * One game as a compact row: Blockari's `GameRow` (c258375), a ~60 px row with a 3 px colour bar, "vs <opponent>" and
 * the net on the right, and a muted caption under it. The same row for History, the profile's recent challenges and
 * anyone's Recent games. With `onOpen` the whole row is one link to the challenge (`d.id`); without it the row is
 * plain and the opponent's name links to their profile (`onProfile`), as on somebody else's page.
 */
export function gameRow(
  d: GameRowData,
  options: { k?: number; onOpen?: (id: string) => void; onProfile?: (username: string) => void } = {},
): HTMLElement {
  const link = options.onOpen !== undefined && d.id !== undefined;
  const root = el(link ? 'a' : 'div', `w21-grow w21-${d.tone}${link ? ' w21-link' : ''}`);
  root.style.setProperty('--k', String(options.k ?? 0));
  root.setAttribute('aria-label', rowLabel(d));
  // Kept for the capture tools, which pick a row by its outcome (won, lost, open ...).
  root.dataset.outcome = d.tone;
  if (link) {
    (root as HTMLAnchorElement).href = `#challenge/${d.id}`;
    root.addEventListener('click', (event) => {
      event.preventDefault();
      options.onOpen?.(d.id!);
    });
  } else {
    root.setAttribute('role', 'group');
  }

  /* -- line one: who, and what it did to the player's $CHAIN ---------------- */
  const opp = d.opponent;
  const profile = opp?.profile;
  const who = el(opp && profile && !link && options.onProfile ? 'a' : 'span', 'w21-gr-who');
  if (opp) {
    who.append(el('span', 'w21-gr-vs', 'vs'), rankBadgeCard(opp.rank ?? { index: UNRANKED, name: 'Unranked', tier: 1 }, 16), el('span', 'w21-gr-name', opp.name));
    if (who instanceof HTMLAnchorElement && profile) {
      who.href = `#profile/${encodeURIComponent(profile)}`;
      who.addEventListener('click', (event) => {
        event.preventDefault();
        options.onProfile?.(profile);
      });
    }
  } else {
    who.append(el('span', 'w21-gr-name w21-plain', d.title ?? 'Challenge'));
  }
  const right = rightSide(d);
  const net = el('span', 'w21-gr-net');
  if (right.kind === 'net') {
    net.classList.add(`w21-${right.tone}`, `w21-${d.tone}`);
    const coinMark = coin();
    coinMark.classList.add('w21-gr-coin');
    net.append(el('b', undefined, right.text), coinMark);
  } else {
    net.classList.add('w21-state', `w21-${d.tone}`);
    if (d.net !== undefined) net.classList.add('w21-zero');
    net.textContent = right.text;
  }

  /* -- line two: scores · stake · Created/Taken · when ---------------------- */
  const cap = el('span', 'w21-gr-cap');
  const dot = (): HTMLElement => {
    const i = el('i', undefined, '·');
    i.setAttribute('aria-hidden', 'true');
    return i;
  };
  for (const chunk of captionChunks(d)) {
    if (chunk.kind === 'text') cap.append(chunk.text);
    else {
      cap.append(el('span', `w21-gr-sc${chunk.mineWon ? ' w21-w' : ''}`, chunk.mine));
      if (chunk.theirs !== null) cap.append(' – ', el('span', `w21-gr-sc${chunk.theirsWon ? ' w21-w' : ''}`, chunk.theirs));
    }
    cap.append(dot());
  }
  const ago = timeAgo(d.at);
  const short = shortAgo(d.at);
  if (ago === short) cap.append(ago);
  else cap.append(el('span', 'w21-gr-long', ago), el('span', 'w21-gr-short', short));

  root.append(who, net, cap);
  return root;
}

/** The list the rows sit in: a plain stack with a 6 px gap (no timeline rail). */
export function gameList(rows: readonly HTMLElement[]): HTMLElement {
  const list = el('div', 'w21-glist');
  list.append(...rows);
  return list;
}

/** My challenges as rows (History, my profile's recent list): the whole row opens `onOpen`. */
export function matchRows(rows: readonly ChallengeCard[], onOpen: (id: string) => void, options: { code?: boolean } = {}): HTMLElement {
  return gameList(rows.map((row, k) => gameRow(matchRowData(row, options), { k: k % 20, onOpen })));
}

/**
 * "2W – 2L" and a 96px win-rate bar — the kit's `.record`, beside the
 * History heading. Nothing at all until something has finished: a bar at 0%
 * over an empty list claims a losing streak that never happened.
 */
function recordOf(won: number, lost: number): HTMLElement | null {
  const played = won + lost;
  if (played === 0) return null;
  const rate = (won / played) * 100;
  const record = el('div', 'w21-record');
  record.title = `${Math.round(rate)}% win rate`;
  const wl = el('span', 'w21-wl');
  wl.append(el('span', 'w21-w', `${won}W`), document.createTextNode(' – '), el('span', 'w21-l', `${lost}L`));
  const bar = el('span', 'w21-bar');
  const fill = el('i');
  fill.style.setProperty('--w', `${rate}%`);
  bar.append(fill);
  record.append(wl, bar);
  return record;
}


/* ================================================================ guide == */

/** Where "this player has been shown the rules" is remembered. */
const STORE_GUIDE = 'wild21.guide.seen';

/**
 * Has this browser been walked through the game before?
 *
 * Wrapped because storage is not always there to be written to — Safari in
 * private browsing throws on `setItem`, and a player who cannot be remembered
 * should still be able to play. The failure mode is being shown the demo twice,
 * which is a great deal better than not being able to start.
 */
export function hasSeenGuide(): boolean {
  if (shownThisSession) return true;
  try {
    return globalThis.localStorage?.getItem(STORE_GUIDE) === '1';
  } catch {
    return false;
  }
}

/**
 * Also kept in memory: where storage will not hold it (a browser blocking site data, a private tab) the
 * demo used to come back on every Play, Create and Take press — "the tutorial covers the create and
 * take cards" — because the answer was lost the moment it was given. Now it is at most once per page.
 */
let shownThisSession = false;

export function rememberGuide(): void {
  shownThisSession = true;
  try {
    globalThis.localStorage?.setItem(STORE_GUIDE, '1');
  } catch {
    /* nothing to be done, and nothing worth breaking a round over */
  }
}

export interface GuideCallbacks {
  /** Stop the demo and get on with the round the player asked for. */
  onSkip(): void;
}

/**
 * The first-run walkthrough: a caption over a real round.
 *
 * This deliberately draws almost nothing. The demo it narrates is an actual
 * game being played on the actual table behind it — a stacked deck dealt by the
 * real engine, thrown by the real choreography, with the real foil on the wild
 * and the real streak border. An earlier version drew a small board out of DOM
 * to stand in for that, and it was wrong in every way a copy is wrong: the
 * wells the wrong size, the piles out of line, no foil, no flashing border.
 * There is nothing to keep in sync now, because there is no copy.
 *
 * So all that is left here is the words, a button, and a scrim dark enough at
 * the bottom to read against and clear at the top so the table shows through.
 */
export class GuideScreen {
  readonly root: HTMLElement;
  readonly skipButton: HTMLButtonElement;

  readonly #caption: HTMLElement;
  readonly #heading: HTMLElement;
  readonly #panel: HTMLElement;
  readonly #fit: PanelFit;
  readonly #reveal: HTMLElement[];
  #tl: gsap.core.Timeline | null = null;

  constructor(callbacks: GuideCallbacks) {
    const { screen, panel, fit } = createScreen('guide');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', 'How to play');

    const eyebrow = el('div', 'w21-guide__eyebrow', 'First time here');
    const title = el('h2', 'w21-guide__title', 'How to play');
    this.#heading = el('div', 'w21-guide__heading');
    this.#heading.append(eyebrow, title);
    this.#caption = el('p', 'w21-guide__caption');

    const actions = el('div', 'w21-actions');
    this.skipButton = el('button', 'w21-btn w21-primary');
    this.skipButton.type = 'button';
    this.skipButton.textContent = 'Deal me in';
    this.skipButton.addEventListener('click', () => callbacks.onSkip());
    actions.append(this.skipButton);

    // The scrim lives on this, not on the panel or the screen. On the screen it
    // faded the whole table; on the panel it painted across the padding that
    // holds it clear of the HUD, dimming the score and the streak chip. On a
    // wrapper it darkens exactly the words and nothing else.
    const body = el('div', 'w21-guide__body');
    body.append(this.#heading, this.#caption, actions);

    this.#panel = panel;
    panel.append(body);
    this.#reveal = [this.#heading, this.#caption, actions];

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') callbacks.onSkip();
    });
  }

  /**
   * Drop the heading once the demo is actually running.
   *
   * "First time here / How to play" earns its place for the second before the
   * first card moves and is dead weight after it — and on a small phone that
   * dead weight is about 60px of table. The caption is doing the teaching by
   * then, so the heading goes and the board gets the room.
   *
   * Called before the felt is measured, so the table is fitted to the panel's
   * final height rather than being re-fitted mid-play — which is the thing that
   * used to take the play sequence down with it.
   */
  compact(): void {
    this.#panel.dataset.compact = 'true';
  }

  /**
   * How much of the screen the narration is covering, in CSS pixels.
   *
   * Measured from whichever edge the narration is against: the top in portrait,
   * the bottom in landscape. The felt is told so it can move the board out of
   * the way — a card landing behind the sentence describing it is worse than a
   * smaller table — and the end the badge uses is left alone.
   */
  inset(): number {
    if (this.root.dataset.active !== 'true') return 0;
    const panel = this.root.firstElementChild;
    if (!panel) return 0;
    const box = panel.getBoundingClientRect();
    // Measured from whichever edge the narration is against.
    return orientation() === 'portrait'
      ? Math.max(0, box.bottom)
      : Math.max(0, (globalThis.innerHeight || 0) - box.top);
  }

  /** The line under the title. Changed as the round below reaches each beat. */
  setCaption(text: string): void {
    if (text === this.#caption.textContent) return;
    this.#caption.textContent = text;
    if (prefersReducedMotion()) return;
    gsap.fromTo(this.#caption, { opacity: 0, y: 5 }, { opacity: 1, y: 0, duration: 0.32 });
  }

  /**
   * @param dismissLabel - what the button says. It promises what happens next,
   *   and what happens next is not the same on a first run (a round is waiting
   *   to be dealt) as on a tutorial the player asked to watch (they go back to
   *   where they pressed it).
   */
  show(dismissLabel = 'Deal me in'): void {
    this.skipButton.textContent = dismissLabel;
    this.root.dataset.active = 'true';
    this.root.dataset.orient = orientation();
    this.#tl?.kill();
    this.#clearHud();

    if (prefersReducedMotion()) {
      gsap.set(this.#reveal, { clearProps: 'transform,opacity' });
      this.#tl = null;
    } else {
      this.#tl = gsap.timeline().fromTo(
        this.#reveal,
        { y: 12, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power3.out', stagger: 0.05, clearProps: 'transform' },
      );
    }

    this.#fit.measure();
    focusSoon(this.skipButton);
  }

  hide(): void {
    this.root.dataset.active = 'false';
  }

  /**
   * Start below the HUD's top bar, measured rather than guessed.
   *
   * The bar carries the score, the clock and — the reason this matters — the
   * streak chip, which is one of the things the walkthrough exists to point
   * at. A fixed padding was wrong on every screen but the one it was written
   * for: too small and it covered the chip, too large and it ate the table.
   */
  #clearHud(): void {
    const panel = this.root.firstElementChild;
    if (!(panel instanceof HTMLElement)) return;
    // Only in portrait, where the narration is under the bar. In landscape it
    // is at the foot of the screen and owes the top nothing.
    if (orientation() !== 'portrait') {
      panel.style.paddingTop = '';
      return;
    }
    const bar = globalThis.document?.querySelector('.w21-hud__top');
    const bottom = bar?.getBoundingClientRect().bottom ?? 0;
    panel.style.paddingTop = bottom > 0 ? `${Math.round(bottom) + 14}px` : '';
  }

  destroy(): void {
    this.#tl?.kill();
    this.#tl = null;
    this.#fit.stop();
    gsap.killTweensOf(this.#reveal);
    this.root.remove();
  }
}

/* ============================================================== audio == */

/**
 * What an audio row can be attached to. `Audio` satisfies this; a test double
 * can too, which is the point — this control has no business importing the
 * audio engine to be laid out.
 */
export interface AudioMixer {
  readonly muted: boolean;
  readonly volume: number;
  readonly musicMuted: boolean;
  readonly musicVolume: number;
  readonly sfxMuted: boolean;
  readonly sfxVolume: number;
  setMuted(muted: boolean): void;
  setVolume(level: number): void;
  setMusicMuted(muted: boolean): void;
  setMusicVolume(level: number): void;
  setSfxMuted(muted: boolean): void;
  setSfxVolume(level: number): void;
}

/**
 * Does moving this slider to `level` also mean "un-mute me"?
 *
 * Exported, and a function rather than two lines inside a listener, because it
 * is the one rule in this control that is a judgement rather than plumbing —
 * and because there is no DOM in this project's test environment, so a rule
 * that stays inside an event handler is a rule that cannot be checked.
 *
 * Dragging a muted row up is a clear statement that you want to hear it again.
 * Dragging it to zero is not: that is someone setting a level, and un-muting
 * them into silence only to have the switch disagree with the slider is worse
 * than leaving it alone.
 */
export function shouldUnmute(level: number, muted: boolean): boolean {
  return muted && level > 0;
}

/* ------------------------------------------------------------- top bar -- */

/**
 * The app mark, top-left on every screen.
 *
 * The icon that already ships — the gold 21 diamond used for the favicon, the
 * home-screen icon and the installed app. Deliberately the same artwork rather
 * than something drawn for the header: a player who has added this to their
 * home screen should meet the identical mark inside it, and a second, nearly
 * identical 21 would be a brand with two answers.
 */

/**
 * The XP ring around a 36 px avatar, as Blockari draws it: an SVG child of the
 * disc, `inset: -5px` so it measures 46 across, stroke 6, track then arc,
 * `pathLength` 1 so the arc's dasharray is the raw progress fraction.
 *
 * Separate from `progressRing()`, which the profile still uses with the older
 * `.w21-ring` geometry; that one moves over when its screen is ported.
 */
export function avatarRing(progress: number): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'w21-avatar-ring');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('aria-hidden', 'true');
  const circle = (cls: string): SVGCircleElement => {
    const c = document.createElementNS(NS, 'circle');
    c.setAttribute('class', cls);
    c.setAttribute('cx', '50'); c.setAttribute('cy', '50'); c.setAttribute('r', '46');
    c.setAttribute('pathLength', '1');
    return c;
  };
  const p = Math.min(1, Math.max(0, progress));
  const arc = circle('w21-arc');
  arc.setAttribute('stroke-dasharray', `${p} ${1 - p}`);
  svg.append(circle('w21-track'), arc);
  return svg;
}

export interface TopBarCallbacks {
  /** The brand. */
  onHome(): void;
  /** The balance pill — the PROFILE, as Blockari's `.balance` links to `/profile`. */
  onBalance?(): void;
  /** The avatar disc — the inbox popover, as Blockari's `.avatar-link` opens it. */
  onAvatar?(): void;
  /** Primary nav. Each is optional; a link with no handler is not rendered. */
  onPlay?(): void;
  onCreate?(): void;
  onTake?(): void;
  /** The header's 2× chip: open the guide at its bonuses. */
  onGuide?(): void;
  onHistory(): void;
  /** Blockari's nav entry, for everyone (History is the signed-in one). */
  onLeaderboard?(): void;
  /**
   * @deprecated The old name, kept so a screen not yet rewired still builds.
   * It was in fact wired to the inbox everywhere, which is why it is retired:
   * the pill and the disc are now named for what they open.
   */
  onProfile?(): void;
}

/**
 * The XP boost from a chest while it runs — Blockari's `BoostChip` (App.tsx): "2×", "XP" and the games left,
 * beside the balance. Hidden when there is none. A tap opens the guide's bonuses.
 */
function boostChip(onOpen?: () => void): HTMLElement {
  const chip = el('a', 'w21-chip w21-boost-chip') as HTMLAnchorElement;
  chip.href = '#';
  chip.hidden = true;
  chip.addEventListener('click', (e) => { e.preventDefault(); onOpen?.(); });
  const paint = (): void => {
    const boost = CHESTS_ENABLED ? chestStore.getState().inventory : null;
    const games = boost?.boostGames ?? 0;
    chip.hidden = !boost || games <= 0;
    if (chip.hidden || !boost) return;
    chip.title = `${boost.boostMultiplier}\u00d7 game XP for ${games} more ${games === 1 ? 'game' : 'games'}`;
    chip.setAttribute('aria-label', `${boost.boostMultiplier}\u00d7 XP bonus, ${games} ${games === 1 ? 'game' : 'games'} left. How it works`);
    chip.replaceChildren(el('b', undefined, `${boost.boostMultiplier}\u00d7`), el('small', undefined, 'XP'), el('i', undefined, String(games)));
  };
  chestStore.subscribe(paint);
  paint();
  return chip;
}

/**
 * A phone's way to the two challenge screens from anywhere — Blockari's `QuickNav`: a small glass pill at the
 * bottom with CREATE and TAKE (the top bar's links are desktop-only). The current one is lit. Its room is
 * reserved by the page's bottom padding, so showing it moves nothing; hidden from 900 px.
 */
export function navDock(
  bar: TopBarCallbacks,
  /** Tapping the lit button: back to the top of the screen it lit for (see `dockTapAction`). */
  toTop?: () => void,
): { root: HTMLElement; show(on: DockTarget | null, visible: boolean): void } {
  let current: DockTarget | null = null;
  const root = el('nav', 'w21-quicknav');
  root.setAttribute('aria-label', 'Challenges');
  root.hidden = true;
  const plus = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>';
  const swords = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4l11 11"></path><path d="M20 4L9 15"></path><path d="M12.5 12.5L6 19M11.5 12.5L18 19"></path><path d="M4.5 16.5l3 3M19.5 16.5l-3 3"></path><path d="M5 21l2-2M19 21l-2-2"></path></svg>';
  const make = (target: DockTarget, label: string, icon: string, on: (() => void) | undefined): HTMLAnchorElement => {
    const a = el('a', 'w21-qn') as HTMLAnchorElement;
    a.href = '#';
    a.innerHTML = icon;
    a.append(document.createTextNode(label));
    a.addEventListener('click', (e) => {
      e.preventDefault();
      if (dockTapAction(current, target) === 'top') toTop?.();
      else on?.();
    });
    return a;
  };
  const create = make('create', 'Create', plus, bar.onCreate);
  const take = make('take', 'Take', swords, bar.onTake);
  root.append(create, take);
  return {
    root,
    show(on, visible) {
      current = on;
      root.hidden = !visible;
      create.classList.toggle('w21-on', on === 'create');
      take.classList.toggle('w21-on', on === 'take');
      if (on === 'create') create.setAttribute('aria-current', 'page'); else create.removeAttribute('aria-current');
      if (on === 'take') take.setAttribute('aria-current', 'page'); else take.removeAttribute('aria-current');
    },
  };
}

/**
 * Unopened chests: Blockari's ChestPill (App.tsx) — a chest button with the
 * count, beside the balance. A tap opens the oldest. Hidden when there are
 * none, except while an arrival's LATER is flying chests into it; it bursts
 * as each lands, and keeps a gentle sheen while chests wait.
 */
function chestPill(): HTMLElement {
  const pill = el('button', 'w21-chest-pill') as HTMLButtonElement;
  pill.type = 'button';
  const n = el('b', 'pill-n', '0');
  const icon = el('span', 'w21-chest-pill-icon');
  pill.append(icon, n);
  // The bump is one-shot: drop the class when it ends so a bar that is shown again does not replay it.
  n.addEventListener('animationend', () => n.classList.remove('pop'));
  let locked = false;
  // A guest's opened chests are held for an account: the pill keeps them in view, locked, and
  // tapping it asks to sign up rather than opening anything.
  pill.addEventListener('click', () => (locked ? openSignup('chest') : openNextChest()));
  let lastBurst = 0;
  let lastN = -1;
  let lastLocked: boolean | null = null;
  const paint = (): void => {
    const a = arrivalStore.getState();
    const guest = viewerStore.getState().guest;
    const held = guest ? chestStore.getState().held.length : 0;
    const unopened = currentPillCount() - 0;
    const count = unopened + held;
    locked = unopened === 0 && held > 0;
    const first = chestStore.getState().unopened[0] ?? (guest ? chestStore.getState().held[0] : undefined);
    const rarity = first?.startRarity ?? a.batch?.chests[0]?.startRarity ?? 'common';
    pill.hidden = count === 0 && !a.stowing;
    pill.className = `w21-chest-pill r-${rarity}${count > 0 ? ' waiting' : ''}${locked ? ' locked' : ''}`;
    pill.setAttribute(
      'aria-label',
      locked
        ? `${count} chest${count === 1 ? '' : 's'} waiting for an account. Sign up to claim.`
        : `${count} unopened chest${count === 1 ? '' : 's'}`,
    );
    if (lastLocked !== locked) {
      icon.replaceChildren(locked ? lockIcon() : chestIcon());
      lastLocked = locked;
    }
    if (count !== lastN) {
      n.textContent = String(count);
      n.classList.remove('pop');
      // Bump only a count that changes in front of the player: not this bar's first paint, and not a bar on a
      // screen that is hidden (every screen owns a bar, and a hidden one would replay the bump when shown).
      const seen = pill.getClientRects().length > 0;
      if (lastN >= 0 && seen) {
        void n.offsetWidth;
        n.classList.add('pop');
      }
      lastN = count;
    }
    if (a.burst > lastBurst) burst(pill);
    lastBurst = a.burst;
  };
  chestStore.subscribe(paint);
  arrivalStore.subscribe(paint);
  viewerStore.subscribe(paint);
  paint();
  return pill;
}

/**
 * The shared shell furniture: brand, primary nav, balance, avatar.
 *
 * The DOM is Blockari's, class for class with a `w21-` prefix — see
 * parity.css. That is not cargo cult: the parity critic grades structure
 * ("same wrappers, chips, buttons, rails") before it grades a single number,
 * and it measures by selector. A bar that is 76 px tall with the right inset
 * but a different tree scores as a different bar.
 *
 *   header.w21-topbar[.scrolled][.brand-hidden]
 *     div.w21-topbar-inner                 (1180 max, flex space-between, gap 16)
 *       a.w21-brand                        (pad 2, gap 8)
 *         img.w21-brand-mark               (28, absolute; shown only under .brand-hidden)
 *         img                              (the wordmark, 52 tall; hidden under .brand-hidden)
 *       nav.w21-topnav                     (desk only, ≥ 900)
 *       div.w21-top-right                  (gap 8)
 *         a.w21-balance > svg.w21-coin + b + small
 *         button.w21-avatar-link > span.w21-avatar.w21-ringed > text + svg.w21-avatar-ring
 *                                 + span.w21-unread-dot
 *
 * One instance per screen rather than one hoisted above them all, because each
 * screen is its own scroll container and a bar owned by a parent would either
 * scroll away with the wrong one or need position juggling per screen. The
 * cost is that `setAccount` has to reach every instance, which `UI` does.
 */
export class TopBar {
  readonly root: HTMLElement;

  readonly #balanceN: HTMLElement;
  readonly #unread: HTMLElement;
  readonly #avatar: HTMLElement;
  readonly #link: HTMLElement;
  readonly #signIn: HTMLButtonElement;
  #toast: HTMLElement | null = null;

  constructor(callbacks: TopBarCallbacks, options: { mark?: boolean } = {}) {
    this.root = el('header', 'w21-topbar');
    this.root.hidden = true;

    // Brand: the mark and the wordmark occupy the same box. The wordmark is
    // always laid out (it is what gives `.brand` its 91×56) and the mark sits
    // absolutely over it; `.brand-hidden` swaps which is visible. That is what
    // stops the bar re-laying out when the hero scrolls in or out.
    const brand = el('a', 'w21-brand') as HTMLAnchorElement;
    brand.href = '#';
    brand.setAttribute('aria-label', '21 Wild home');
    brand.addEventListener('click', (e) => { e.preventDefault(); callbacks.onHome(); });
    const mark = el('img', 'w21-brand-mark') as HTMLImageElement;
    mark.src = `${import.meta.env.BASE_URL}mark.svg`;
    mark.width = 28; mark.height = 28; mark.alt = ''; mark.decoding = 'async';
    mark.setAttribute('aria-hidden', 'true');
    const word = el('img') as HTMLImageElement;
    word.src = `${import.meta.env.BASE_URL}logo@1x.webp`;
    word.width = 88; word.height = 53; word.alt = '21 Wild'; word.decoding = 'async';
    word.draggable = false;
    brand.append(mark, word);
    brand.hidden = options.mark === false;

    const nav = el('nav', 'w21-topnav');
    nav.setAttribute('aria-label', 'Primary');
    const link = (label: string, on?: () => void): void => {
      if (!on) return;
      const a = el('a', undefined, label) as HTMLAnchorElement;
      a.href = '#';
      a.dataset.route = label.toLowerCase();
      a.addEventListener('click', (e) => { e.preventDefault(); on(); });
      nav.append(a);
    };
    link('Play', callbacks.onPlay);
    link('Create', callbacks.onCreate);
    link('Take', callbacks.onTake);
    link('History', callbacks.onHistory);
    link('Leaderboard', callbacks.onLeaderboard);

    // The balance goes to history and the avatar to the profile: the pill is
    // what you have, the disc is who you are. Sending both to the same place
    // wastes the only two persistent targets in the shell.
    const balance = el('a', 'w21-balance') as HTMLAnchorElement;
    balance.href = '#';
    balance.addEventListener('click', (e) => {
      e.preventDefault();
      (callbacks.onBalance ?? callbacks.onHistory)();
    });
    this.#balanceN = el('b');
    balance.append(coin(), this.#balanceN, el('small', undefined, '$CHAIN'));

    this.#link = el('button', 'w21-avatar-link');
    (this.#link as HTMLButtonElement).type = 'button';
    this.#link.setAttribute('aria-haspopup', 'dialog');
    this.#link.setAttribute('aria-expanded', 'false');
    this.#link.addEventListener('click', () =>
      (callbacks.onAvatar ?? callbacks.onProfile ?? callbacks.onHistory)());
    this.#avatar = el('span', 'w21-avatar w21-ringed');
    this.#avatar.setAttribute('aria-hidden', 'true');
    // Present only while something is unread — `.unread-dot` has no hidden
    // state in Blockari, the element is simply not rendered. `hidden` here.
    this.#unread = el('span', 'w21-unread-dot');
    this.#unread.hidden = true;
    this.#link.append(this.#avatar, this.#unread);
    // The equipped frame rings the avatar (Blockari's `.avatar-link.framed.f-<id>`).
    const paintFrame = (): void => {
      const gear = CHESTS_ENABLED ? chestStore.getState().inventory : null;
      for (const c of [...this.#link.classList]) if (c.startsWith('w21-f-')) this.#link.classList.remove(c);
      const cls = frameClassOf(gear?.frame ?? null).trim().split(' ').filter(Boolean);
      this.#link.classList.toggle('w21-framed', cls.length > 0);
      for (const c of cls) if (c !== 'w21-framed') this.#link.classList.add(c);
    };
    chestStore.subscribe(paintFrame);
    paintFrame();

    // A guest's way back into an account it made earlier (a guest cannot be signed back in to, so
    // a returning player has to be able to leave it). Shown for guests only.
    this.#signIn = el('button', 'w21-top-signin') as HTMLButtonElement;
    this.#signIn.type = 'button';
    this.#signIn.setAttribute('aria-label', 'Sign in');
    // The word on a desk; on a phone the bar has no room for it beside the balance, so the door alone.
    this.#signIn.innerHTML =
      '<span class="w21-top-signin-label">Sign in</span>' +
      '<svg class="w21-top-signin-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 17l5-5-5-5M15 12H3M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4"/></svg>';
    this.#signIn.hidden = true;
    this.#signIn.addEventListener('click', () => openSignIn());

    // Blockari's order: the balance, the boost chip, the chest pill, then the avatar.
    const right = el('div', 'w21-top-right');
    right.append(this.#signIn, balance);
    if (CHESTS_ENABLED) right.append(boostChip(callbacks.onGuide), chestPill());
    right.append(this.#link);

    const inner = el('div', 'w21-topbar-inner');
    inner.append(brand, nav, right);
    this.root.append(inner);
  }

  /** The unread count on this bar's avatar. */
  setUnread(count: number): void {
    const n = Math.max(0, Math.floor(count));
    // Blockari's badge: the count above 1 ("9+" above 9), a bare 10 px dot
    // at exactly 1 (the text is empty, and `.unread-dot:empty` draws the
    // dot), nothing at 0.
    this.#unread.hidden = n === 0;
    this.#unread.textContent = n > 9 ? '9+' : n > 1 ? String(n) : '';
    this.#link.classList.toggle('w21-has-unread', n > 0);
  }

  /** The number in the pill and nothing else — driven by the balance display while a coin flight holds it. */
  setBalance(balance: number): void {
    this.#balanceN.textContent = formatScore(balance);
  }

  setAccount(
    account:
      | { username: string; balance: number; rank?: RankView; xpProgress?: number }
      | null,
  ): void {
    if (!account) {
      this.root.hidden = true;
      return;
    }
    this.#balanceN.textContent = formatScore(account.balance);
    const guest = (account as { isGuest?: boolean }).isGuest === true;
    this.#signIn.hidden = !guest;
    this.root.classList.toggle('w21-guest', guest);
    this.#link.setAttribute('aria-label', `Notifications · ${account.username}`);

    // Initials, hue and rank on the disc; the ring is an SVG child of the disc,
    // exactly as Blockari lays it out, so `.avatar-ring`'s inset -5 measures
    // the 46 the spec asks for against a 36 disc.
    // Always, for anyone signed in. It used to require `account.rank`, and a
    // brand-new guest reaching the home screen before their rank had resolved
    // got no ring at all — which is every player's first visit.
    const rank = account.rank?.index ?? 0;
    this.#avatar.className = `w21-avatar w21-ringed w21-rank-${rank}`;
    this.#avatar.style.setProperty('--hue', String(hueOf(account.username)));
    this.#avatar.replaceChildren(
      document.createTextNode(initialsOf(account.username)),
      avatarRing(account.xpProgress ?? 0),
    );
    this.root.hidden = false;
  }

  /**
   * `.scrolled` once the owning screen has moved more than 40 px (Blockari's
   * SCROLLED_PX): the glass gets more ink under it so the bar stays legible
   * over content. Driven by the screen's own scroll position, since each
   * screen is its own scroller.
   */
  setScrolled(scrolled: boolean): void {
    this.root.classList.toggle('w21-scrolled', scrolled);
  }

  /**
   * `.active` on the nav link for the route being shown — HISTORY lit on the
   * history screen — as Blockari lights the current route's link. Every bar
   * is told, since every screen has one and the bar cannot know which screen
   * it is on.
   */
  setActive(route: string): void {
    for (const a of this.root.querySelectorAll<HTMLAnchorElement>('.w21-topnav a')) {
      const on = a.dataset.route === route;
      a.classList.toggle('w21-active', on);
      if (on) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    }
  }

  /**
   * `.brand-hidden` while the hero wordmark is on screen: the 28 px mark holds
   * the brand slot and the wordmark in the bar hides, so the name is never on
   * screen twice. Only the home screen has a hero, so only it calls this.
   */
  setBrandHidden(hidden: boolean): void {
    this.root.classList.toggle('w21-brand-hidden', hidden);
  }

  /** The daily top-up toast, docked under the balance. */
  showDailyGrant(amount: number): void {
    this.#toast?.remove();
    const toast = el('div', 'w21-topup');
    toast.setAttribute('role', 'status');
    toast.append(
      coin(),
      el('span', 'w21-topup__n', `+${formatScore(amount)}`),
      el('span', 'w21-topup__unit', '$CHAIN daily top-up'),
    );
    this.root.append(toast);
    this.#toast = toast;
    window.setTimeout(() => {
      if (this.#toast !== toast) return;
      toast.dataset.out = 'true';
      window.setTimeout(() => {
        toast.remove();
        if (this.#toast === toast) this.#toast = null;
      }, 320);
    }, 4000);
  }
}

/* -------------------------------------------------------------- ranks -- */

/**
 * The rank badge.
 *
 * A faceted solid in the rank colour: a lit top face, two shaded sides, one
 * specular, and the tier as pips beneath. The same isometric block Blockari
 * uses, deliberately — this is the one piece of the system that is supposed to
 * look identical across the titles, because it is the piece a player carries
 * between them. The rank COLOURS are ours; the shape is shared.
 *
 * `size` is the badge's width in CSS pixels. Pips are dropped below 24px, where
 * five of them are smaller than the gaps between them and read as noise rather
 * than as a count.
 */
/** `#rrggbb` → [r, g, b]. */
function rgbOf(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * The three face colours for a rank: one hue, three luminances.
 *
 * This has been wrong twice, in opposite directions, and both mistakes are
 * worth naming because they are the two obvious ways to shade a colour and
 * neither works.
 *
 * Drawing the rank at reduced OPACITY over the dark card multiplies toward the
 * ground, which turns a cream into khaki — the top rank came out butter, sand
 * and mud. Mixing toward an explicit warm shadow instead fixed the pale ranks
 * and broke every cool one: Deuce's right face swung from 232° to 36°, so a
 * slate badge grew a mud-brown side and every badge became a two-hue object.
 *
 * A solid is three luminances of ONE hue. So the faces are computed in HSL
 * with the hue and saturation untouched and only the lightness scaled. Whatever
 * the rank, its shaded faces are unmistakably the same colour as its lit one.
 */
function faces(index: number): { top: string; left: string; right: string } {
  // No rank on record: grey, with the saturation actually at zero.
  //
  // The first attempt set `--w21-rank` to a slate on the wrapper, which did
  // nothing to the cube — these three faces are computed here in JS from
  // `rankPalette`, so the solid stayed Deuce's blue at 85% opacity. Measured
  // beside the real Deuce badge it was the same cube slightly dimmed, and a
  // player reads a rank off it, which is the one thing this mark exists not to
  // say. Dimming a hue is not the same as having no hue.
  if (index === UNRANKED) {
    return { top: '#8e9099', left: '#5b5d66', right: '#3d3f48' };
  }

  // The top rank is hand-set.
  //
  // Everything else derives correctly, but white-gold is not a lightness of a
  // yellow: scaling #FFF0B8 in HSL keeps saturation at 100% and produces a
  // lighter King rather than a colder metal. The one rank whose whole point is
  // that it does not look like the others is the one that has to be stated.
  if (index === RANK_NAMES.length - 1) {
    return { top: '#f4f1ff', left: '#f3e6bd', right: '#c9962e' };
  }

  const base = rankPalette[index] ?? rankPalette[0]!;
  const [r, g, b] = rgbOf(base).map((v) => v / 255) as [number, number, number];

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  let hue = 0;
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  const at = (lightness: number): string =>
    `hsl(${hue.toFixed(1)} ${(sat * 100).toFixed(1)}% ${(Math.min(1, lightness) * 100).toFixed(1)}%)`;

  return {
    // The lit face is lifted toward white without leaving its hue — a pale
    // rank has to read as light here rather than as more of itself.
    top: at(l + (1 - l) * 0.5),
    left: at(l * 0.72),
    right: at(l * 0.5),
  };
}

/**
 * The index that means "no account behind this name".
 *
 * Deliberately out of the palette's range rather than 0: rank 0 is Deuce, a
 * rank somebody holds, and every lookup that falls back to it would quietly
 * dress an anonymous player as one.
 */
export const UNRANKED = -1;

export function rankBadge(rank: { index: number; name: string; tier: number }, size = 24): HTMLElement {
  const wrap = el('span', 'w21-rank');
  if (rank.index === UNRANKED) {
    wrap.dataset.unranked = 'true';
    wrap.style.setProperty('--w21-rank', '#8e9099');
    wrap.style.setProperty('--w21-rank-rgb', '142 144 153');
  } else {
    wrap.style.setProperty('--w21-rank', `var(--w21-rank-${rank.index})`);
    wrap.style.setProperty('--w21-rank-rgb', `var(--w21-rank-${rank.index}-rgb)`);
  }
  wrap.style.setProperty('--w21-rank-size', `${size}px`);
  // The top rank is the only one that moves.
  if (rank.index === RANK_NAMES.length - 1) wrap.dataset.legend = 'true';
  // Cast light is for the hero only — see the note in the stylesheet.
  if (size >= 56) wrap.dataset.hero = 'true';
  wrap.title = `${rank.name} ${tierNumeral(rank.tier)}`;

  const gem = el('span', 'w21-rank__gem');
  gem.setAttribute('aria-hidden', 'true');
  // Lit from the upper LEFT: top face brightest, left face mid, right face
  // dark, with a rim on the two edges the light actually catches and one soft
  // specular high on the top face. The previous version was lit from the
  // upper right with a hard parallelogram highlight and no rim, which read as
  // a flat sticker rather than a solid.
  //
  // A shorter, wider solid than before, so it reads as a stack of chips on a
  // table rather than a tower — the shape is ours, the lighting is shared.
  const face = faces(rank.index);
  gem.innerHTML = [
    // Cube proportions: the side is close to the top edge rather than a slab.
    // The side faces are the height of the top rhombus, so the solid reads as
    // a cube rather than a slab: top 2→16 is 14 tall, sides 16→30 are 14 too.
    '<svg viewBox="0 0 30 32" width="100%" height="100%" fill="none">',
    '<defs>',
    // A TIGHT specular, not a wash across the whole top face. At r=0.6 it was
    // a wide radial gradient and the face read as a gradient rather than as a
    // solid catching a light.
    `<radialGradient id="w21spec${rank.index}" cx="0.28" cy="0.18" r="0.26">`,
    '<stop offset="0" stop-color="#fff" stop-opacity="0.85"/>',
    '<stop offset="1" stop-color="#fff" stop-opacity="0"/>',
    '</radialGradient>',
    '</defs>',
    `<path d="M15 2 L28 9 L15 16 L2 9 Z" fill="${face.top}"/>`,
    // Left face, toward the light. Right face away from it — measured at 0.17
    // against the card when it was authored at 0.26, because both sit on a
    // dark ground; lifted so the solid does not lose its third face.
    `<path d="M2 9 L15 16 L15 30 L2 23 Z" fill="${face.left}"/>`,
    `<path d="M28 9 L15 16 L15 30 L28 23 Z" fill="${face.right}"/>`,
    // Rim light on the two edges the light catches.
    '<path d="M15 2 L2 9" stroke="#fff" stroke-opacity="0.55" stroke-width="1.1"/>',
    '<path d="M2 9 L2 23" stroke="#fff" stroke-opacity="0.32" stroke-width="1.1"/>',
    // One soft specular, high on the top face.
    `<path d="M15 2 L28 9 L15 16 L2 9 Z" fill="url(#w21spec${rank.index})"/>`,
    '</svg>',
  ].join('');

  wrap.append(gem);

  // Pips only on the big emblem. Below 56px five of them are smaller than the
  // gaps between them and stop reading as a count — they become a texture
  // under the badge, which is worse than no tier at all.
  if (size >= 56) {
    const pips = el('span', 'w21-rank__pips');
    for (let i = 0; i < RANK_SPAN; i++) {
      const pip = el('span', 'w21-rank__pip');
      pip.dataset.on = String(i < rank.tier);
      pips.append(pip);
    }
    wrap.append(pips);
  }

  return wrap;
}

/** "MASON III" — the rank, spelled the way it is spoken. */
export function rankName(rank: { name: string; tier: number }): HTMLElement {
  return el('span', 'w21-rankname', `${rank.name} ${tierNumeral(rank.tier)}`.toUpperCase());
}

/**
 * The account chip: "XP LV 12".
 *
 * Says XP out loud, every time, because the HUD's in-match chip says "LV 5" and
 * the two numbers are unrelated. A player who reads one as the other will think
 * they lost eight levels between the table and the results screen.
 */
export function xpChip(xpLevel: number, rankIndex?: number): HTMLElement {
  const chip = el('span', 'w21-xpchip');
  // The chip sets its OWN rank colour. It used to read `--w21-rank` from an
  // ancestor, which the profile sheet and the XP beat happen to set and the
  // take rows, the ladder and the history cards do not — so the same component
  // rendered rank-coloured in two places and white in three.
  const index = rankIndex ?? rankFor(xpLevel).index;
  chip.style.setProperty('--w21-rank', `var(--w21-rank-${index})`);
  chip.style.setProperty('--w21-rank-rgb', `var(--w21-rank-${index}-rgb)`);
  chip.append(el('span', 'w21-xpchip__k', 'XP LV'), el('span', 'w21-xpchip__n', String(xpLevel)));
  return chip;
}

/**
 * Badge, name and chip as ONE unit.
 *
 * Used everywhere a player is named, so that a rank never drifts away from the
 * person it belongs to when a layout reflows. The chip is optional because the
 * VS block and the open-challenge rows want it and a history card does not have
 * the width.
 */
export function playerTag(
  rank: { index: number; name: string; tier: number },
  name: string,
  options: { size?: number; xpLevel?: number } = {},
): HTMLElement {
  const tag = el('span', 'w21-ranktag');

  // The badge and the name are ONE unit that never comes apart.
  //
  // They used to be siblings of the chip in a single wrapping flex, so in a
  // narrow column the wrap fell between the badge and the name — the badge
  // ended up on a line of its own beside the NEXT player's name, which read as
  // that player's rank. Whatever else has to give, these two stay together.
  const id = el('span', 'w21-ranktag__id');
  id.append(rankBadgeCard(rank, options.size ?? 24), el('span', 'w21-ranktag__name', name));
  tag.append(id);

  // The chip is what wraps, under the pair, when there is no room beside it.
  if (options.xpLevel !== undefined) tag.append(xpChip(options.xpLevel, rank.index));
  return tag;
}

/**
 * The progress ring around an avatar.
 *
 * Two SVG circle strokes with `pathLength="1"`, not a conic-gradient under a
 * radial mask. The gradient version is fewer lines and looks identical on a
 * desktop — and iOS Safari ignores the mask and paints a grey wedge over the
 * avatar, which is most of the audience for this game.
 *
 * @param progress - 0..1 toward the next XP level.
 */
export function progressRing(rankIndex: number, progress: number): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'w21-ring');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('aria-hidden', 'true');

  const circle = (cls: string, dash: string): SVGCircleElement => {
    const c = document.createElementNS(NS, 'circle');
    c.setAttribute('class', cls);
    c.setAttribute('cx', '50');
    c.setAttribute('cy', '50');
    c.setAttribute('r', '46');
    c.setAttribute('fill', 'none');
    c.setAttribute('pathLength', '1');
    c.setAttribute('stroke-dasharray', dash);
    c.setAttribute('stroke-linecap', 'round');
    return c;
  };

  // A sliver at zero, so a player who has just levelled up sees a ring rather
  // than a bare track and wonders whether it is broken.
  const shown = Math.min(1, Math.max(0.03, progress));
  svg.append(circle('w21-ring__track', '1 0'), circle('w21-ring__arc', `${shown} 1`));
  svg.style.setProperty('--w21-rank', `var(--w21-rank-${rankIndex})`);
  svg.style.setProperty('--w21-rank-rgb', `var(--w21-rank-${rankIndex}-rgb)`);
  return svg;
}

/* ------------------------------------------------------- staked challenges -- */

/**
 * The level chip — "LV 7" in the level colour.
 *
 * The same component everywhere a level is shown outside the HUD: the waiting
 * room, the history list, the breakdown. One implementation, so a level never
 * looks like two different things in two places.
 *
 * `progress` fills the chip from the left, exactly as the HUD pill does. Pass
 * null where there is nothing to be part-way through — a finished round's level
 * is a fact, not a bar creeping toward the next one.
 */
export function levelChip(
  level: number,
  progress: number | null = null,
  options: { short?: boolean } = {},
): HTMLElement {
  const chip = el('span', 'w21-lvchip');
  setTone(chip, 'azure');
  if (progress !== null) {
    chip.classList.add('w21-lvchip--bar');
    // Floored at 6% so the fill always reads as a bar rather than a dot: a
    // player who has just levelled up is at 0% and would otherwise see nothing.
    chip.style.setProperty('--w21-lv-fill', `${Math.max(6, progress * 100).toFixed(1)}%`);
  }
  // "MATCH LV" in the shell, where it sits inches from the account's "XP LV"
  // and the two numbers are unrelated. The engine HUD keeps the bare "LV":
  // there is nothing to confuse it with on a board mid-round, and the band is
  // too tight to spend six characters saying so.
  //
  // `short` is the same argument: inside a history card the rank is carried by
  // the badge beside it and there is no XP chip anywhere on the row, so "LV"
  // is unambiguous — and two chips reading "MATCH LV 6" do not fit in two
  // half-width columns at 390 without colliding.
  // Always "MATCH LV". The short form was introduced to fit two chips in two
  // half-width columns, and it bought that at the cost of the very ambiguity
  // the words were added to remove — "LV 6" beside an "XP LV 12" is two
  // dialects for one idea. The columns give way instead.
  chip.append(el('span', 'w21-lvchip__label', 'MATCH LV'), el('span', 'w21-lvchip__n', String(level)));
  return chip;
}

/**
 * Whether an opponent has a real identity to show.
 *
 * "Anonymous" is the name stored on every result from before accounts existed.
 * There is no account behind it, so the rank that would be drawn beside it is
 * a rank nobody earned.
 */
function isNamed(player: { name: string } | null): boolean {
  return player !== null && !/^anonymous$/i.test(player.name.trim());
}


export interface CreateChallengeCallbacks {
  /**
   * The shell bar's targets, wired once in `mountUI` and handed to every
   * screen, so the bar is the same bar on every route. Without this each
   * screen built its own from its own callbacks, and the History screen's had
   * nothing on it: balance and avatar no-ops, brand hidden, the only way out a
   * Back button under thirty cards.
   */
  bar?: TopBarCallbacks;
  /** Open a challenge at this stake; `useTicket` pays it with a free-entry ticket. */
  onCreate(entryFee: number, isPrivate: boolean, useTicket: boolean): void;
  onBack(): void;
  onHistory(): void;
  onHome(): void;
  onProfile(): void;
  /** One of the player's own challenges, from the "N open" sheet. */
  onOpenChallenge?(id: string): void;
}

/** The stake selected when the screen is first opened. Matches Blockari's. */
const DEFAULT_FEE = FIRST_FEE;

/**
 * The free-entry ticket row — Blockari's `.ticket-toggle` (FeePicker): the ticket's stub with its rung, USE TICKET,
 * ×held and a knob. Shown only while a ticket is held at the chosen wager, off until the player turns it on (a
 * ticket is kept until used, so it is never spent for them).
 */
let ticketSeq = 0;

/** Blockari's ticket stub: the USE TICKET row's glyph (with the rung on it), and the small one under a chip. */
function ticketGlyph(fee?: number): SVGSVGElement {
  const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  glyph.setAttribute('class', 'w21-ticket-glyph');
  glyph.setAttribute('viewBox', '0 0 48 32');
  glyph.setAttribute('aria-hidden', 'true');
  // A gradient id is document-wide: one per glyph, or a hidden screen's copy (display: none draws no defs)
  // starves every other instance of its fill.
  const gid = `w21-tk-face-${++ticketSeq}`;
  glyph.innerHTML =
    `<defs><linearGradient id="${gid}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#7df5dc"></stop><stop offset="1" stop-color="#1fb89c"></stop></linearGradient></defs>` +
    `<path d="M4 4h40v8a4 4 0 0 0 0 8v8H4v-8a4 4 0 0 0 0-8z" fill="url(#${gid})" stroke="#0b3d36" stroke-width="1.5"></path>` +
    '<path d="M31 6v20" stroke="#0b3d36" stroke-width="1.2" stroke-dasharray="2 2"></path>' +
    (fee !== undefined ? `<text x="17.5" y="21" text-anchor="middle" class="w21-ticket-fee">${fee}</text>` : '');
  return glyph;
}

function ticketSwitch(onToggle: () => void): { root: HTMLButtonElement; paint(held: number, on: boolean, fee?: number): void } {
  const root = el('button', 'w21-ticket-toggle') as HTMLButtonElement;
  root.type = 'button';
  root.setAttribute('role', 'switch');
  root.hidden = true;
  root.addEventListener('click', onToggle);
  return {
    root,
    paint(held, on, fee) {
      root.hidden = held <= 0;
      root.classList.toggle('w21-on', on);
      root.setAttribute('aria-checked', String(on));
      const glyph = ticketGlyph(fee);
      const knob = el('span', 'w21-tt-knob');
      knob.setAttribute('aria-hidden', 'true');
      root.replaceChildren(glyph, el('span', 'w21-tt-label', 'Use ticket'), el('span', 'w21-tt-count', `\u00d7${Math.max(held, on ? 1 : 0)}`), knob);
    },
  };
}

/** One quiet line crossing to the sibling page — Blockari's `CrossLink`: "Rather take one? TAKE A CHALLENGE →". */
function crossLink(lead: string, label: string, on: (() => void) | undefined): HTMLElement {
  const p = el('p', 'w21-cross-link');
  p.append(document.createTextNode(`${lead} `));
  const a = el('a', undefined, `${label} `) as HTMLAnchorElement;
  a.href = '#';
  a.insertAdjacentHTML('beforeend', ICON_ARROW_RIGHT);
  a.addEventListener('click', (e) => { e.preventDefault(); on?.(); });
  p.append(a);
  return p;
}

/**
 * Blockari's `CreatedCount`: "You have created 7 challenges · 2 open." / "You have taken 5 challenges." With `onOpenList`
 * (Create), a non-zero "N open" is the teal link that opens all of the player's own open challenges; with none open it
 * is plain text.
 */
class CountLine {
  readonly root = el('p', 'w21-created-count w21-t-caption');
  constructor(
    private readonly kind: 'created' | 'taken',
    private readonly onOpenList?: () => void,
  ) {
    this.root.hidden = true;
  }
  /** What is drawn now, so a re-read that found the same numbers repaints nothing (and keeps focus on the link). */
  #drawn = '';
  set(counts: ChallengeCounts | null): void {
    if (!counts) {
      this.root.hidden = true;
      this.#drawn = '';
      return;
    }
    const sign = `${counts.created}|${counts.taken}|${counts.open}`;
    if (sign === this.#drawn && !this.root.hidden) return;
    this.#drawn = sign;
    const n = this.kind === 'created' ? counts.created : counts.taken;
    this.root.replaceChildren(document.createTextNode(this.kind === 'created' ? 'You have created ' : 'You have taken '), el('b', undefined, n.toLocaleString()), document.createTextNode(` ${n === 1 ? 'challenge' : 'challenges'}`));
    if (this.kind === 'created' && counts.created > 0) {
      this.root.append(document.createTextNode(' \u00b7 '));
      const linkLabel = openLinkLabel(counts.open, this.onOpenList !== undefined);
      if (this.onOpenList && linkLabel) {
        const link = el('button', 'w21-open-link') as HTMLButtonElement;
        link.type = 'button';
        link.setAttribute('aria-label', linkLabel);
        link.append(el('b', undefined, counts.open.toLocaleString()), document.createTextNode(' open'));
        link.addEventListener('click', this.onOpenList);
        this.root.append(link);
      } else {
        this.root.append(el('b', undefined, counts.open.toLocaleString()), document.createTextNode(' open'));
      }
    }
    this.root.append(document.createTextNode('.'));
    this.root.hidden = false;
  }
}

/** What one quick tile shows under its amount, and whether it can be tapped. */
interface WagerCell {
  /** The small line under the amount ("2 open", "1 yours", "ticket"). */
  hint?: string;
  /** The held-ticket glyph follows the hint on the same line ("1 yours" + ticket). */
  glyph?: boolean;
  disabled?: boolean;
  title?: string;
  /** The chip's accessible name when its caption adds something ("10, 1 of yours open"); else its own text names it. */
  ariaLabel?: string;
}

/**
 * The WAGER row of Create and Take — Blockari's `Segmented` in `.fee-chips`: the four quick wagers and a fifth CUSTOM
 * tile, five equal cells in one control. The tiles are persistent (a repaint updates them in place, so a focused input
 * keeps its focus). On Create the CUSTOM cell turns into `input` in the same cell; on Take it is a plain filter tile.
 */
class WagerRow {
  readonly root = el('div', 'w21-seg w21-warm w21-fee-chips');
  readonly #thumb = el('span', 'w21-seg-thumb');
  #quick: HTMLButtonElement[] = [];
  readonly #customBtn = el('button', 'w21-seg-item w21-seg-custom') as HTMLButtonElement;
  readonly #inputCell = el('div', 'w21-seg-item w21-seg-input w21-seg-custom');
  readonly #inputHint = el('small');
  #fees: readonly number[] = [];
  #picks: Array<() => void> = [];

  constructor(
    private readonly on: { quick(fee: number): void; custom(): void },
    input?: HTMLInputElement,
  ) {
    this.root.setAttribute('role', 'radiogroup');
    this.root.setAttribute('aria-label', 'Quick wager');
    this.#thumb.setAttribute('aria-hidden', 'true');
    this.#customBtn.type = 'button';
    this.#customBtn.setAttribute('role', 'radio');
    this.#customBtn.addEventListener('click', () => this.on.custom());
    this.#inputCell.setAttribute('role', 'radio');
    this.#inputCell.hidden = true;
    if (input) this.#inputCell.append(input);
    this.#inputCell.append(this.#inputHint);
    this.root.addEventListener('keydown', (event) => this.#onKey(event));
  }

  /** Arrow keys move the choice and the focus (a radiogroup); keys typed into the CUSTOM input are the input's. */
  #onKey(event: KeyboardEvent): void {
    if ((event.target as HTMLElement).tagName === 'INPUT') return;
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    const tiles = [...this.#quick, this.#customBtn];
    const at = tiles.findIndex((t) => t.getAttribute('aria-checked') === 'true');
    for (let k = 1; k <= tiles.length; k++) {
      const next = (((at < 0 ? (step > 0 ? -1 : 0) : at) + step * k) % tiles.length + tiles.length) % tiles.length;
      const tile = tiles[next];
      if (!tile || tile.disabled) continue;
      event.preventDefault();
      tile.focus();
      this.#picks[next]?.();
      return;
    }
  }

  /**
   * `selected` is a quick tile's amount, 'custom', or null (nothing lit: a legacy or unaffordable amount).
   * `custom.editing` shows the input cell in place of the button.
   */
  paint(p: {
    fees: readonly number[];
    selected: number | 'custom' | null;
    cell(fee: number): WagerCell;
    custom: { label: string; hint: string; disabled?: boolean; editing?: boolean; ariaLabel?: string };
    disabled: boolean;
  }): void {
    if (this.#fees.join() !== p.fees.join() || this.#quick.length !== p.fees.length) {
      this.#fees = p.fees;
      this.#quick = p.fees.map((fee) => {
        const tile = el('button', 'w21-seg-item') as HTMLButtonElement;
        tile.type = 'button';
        tile.setAttribute('role', 'radio');
        tile.addEventListener('click', () => this.on.quick(fee));
        return tile;
      });
      this.#picks = [...p.fees.map((fee) => () => this.on.quick(fee)), () => this.on.custom()];
      this.root.replaceChildren(this.#thumb, ...this.#quick, this.#customBtn, this.#inputCell);
    }
    const n = p.fees.length + 1;
    const index = p.selected === 'custom' ? p.fees.length : p.selected === null ? -1 : p.fees.indexOf(p.selected);
    this.root.classList.toggle('w21-off', index < 0);
    this.root.style.setProperty('--n', String(n));
    this.root.style.setProperty('--i', String(Math.max(0, index)));
    const firstOpen = this.#quick.findIndex((_, i) => !p.cell(p.fees[i] ?? 0).disabled);
    p.fees.forEach((fee, i) => {
      const tile = this.#quick[i];
      if (!tile) return;
      const cell = p.cell(fee);
      const on = fee === p.selected;
      tile.setAttribute('aria-checked', String(on));
      tile.tabIndex = on || (index < 0 && i === Math.max(0, firstOpen)) ? 0 : -1;
      tile.disabled = p.disabled || cell.disabled === true;
      if (cell.title) tile.title = cell.title;
      else tile.removeAttribute('title');
      if (cell.ariaLabel) tile.setAttribute('aria-label', cell.ariaLabel);
      else tile.removeAttribute('aria-label');
      const small = el('small');
      if (cell.hint) {
        small.textContent = cell.hint;
        if (cell.glyph) {
          // "N yours" with a held ticket: the ticket as its small glyph on the same line, not the word.
          small.classList.add('w21-hint-both');
          small.append(ticketGlyph());
        }
      }
      tile.replaceChildren(formatScore(fee), ...(cell.hint ? [small] : []));
    });
    const custom = p.selected === 'custom';
    this.#customBtn.setAttribute('aria-checked', String(custom));
    this.#customBtn.tabIndex = custom || (index < 0 && firstOpen < 0) ? 0 : -1;
    this.#customBtn.disabled = p.disabled || p.custom.disabled === true;
    if (p.custom.ariaLabel) this.#customBtn.setAttribute('aria-label', p.custom.ariaLabel);
    else this.#customBtn.removeAttribute('aria-label');
    this.#customBtn.replaceChildren(p.custom.label, el('small', undefined, p.custom.hint));
    this.#inputCell.setAttribute('aria-checked', String(custom));
    this.#inputHint.textContent = p.custom.hint;
    const editing = p.custom.editing === true;
    this.#inputCell.hidden = !editing;
    this.#customBtn.hidden = editing;
  }
}

/**
 * Create a challenge: pick a stake, pick who can take it, deal.
 *
 * Laid out as the shared Chain Games sheet — heading with the mode's CTA as a
 * chip beside it, an explainer that says plainly what pressing the button will
 * do, a segmented fee picker, the pot restated, the privacy switch, the action,
 * and a footnote about the clock. A player who has seen this in one title finds
 * the same furniture in the same order here.
 *
 * The one thing it must not be possible to get wrong is what this costs and
 * what it pays, so the pot card restates both in words every time the picker
 * moves rather than leaving the player to double a number.
 */
export class CreateChallengeScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;

  readonly #fit: PanelFit;
  readonly #pot = el('div', 'w21-pot');
  /** The CUSTOM tile's input: it lives in the tile's own cell (see `WagerRow`) and is never rebuilt, so it keeps focus. */
  readonly #amount = el('input', 'w21-seg-amount') as HTMLInputElement;
  /** One validation line under the row, drawn only when there is something to say. */
  readonly #feeHint = el('div', 'w21-fee-hint w21-bad');
  readonly #wager = new WagerRow(
    {
      quick: (fee) => {
        if (this.#busy) return;
        // A quick tile deselects CUSTOM (the typed amount is kept for re-open).
        this.#customSel = false;
        this.#editing = false;
        this.#fee = fee;
        this.#error.textContent = '';
        this.#paint();
      },
      custom: () => {
        if (this.#busy) return;
        // CUSTOM becomes the input in place; the last typed amount comes back with it.
        this.#customSel = true;
        this.#editing = true;
        this.#fee = parseFeeText(this.#text);
        this.#error.textContent = '';
        this.#paint();
        this.#amount.focus();
      },
    },
    this.#amount,
  );
  /** CUSTOM is the selected tile; `#editing`: it is an input right now; `#text`: the last typed amount. */
  #customSel = false;
  #editing = false;
  #text = '';
  readonly #privacy: ReturnType<typeof kitSwitch>;
  readonly #confirm: HTMLButtonElement;
  readonly #error = el('p', 'w21-form__error');

  #bounds: FeeBounds = DEFAULT_BOUNDS;
  #fee = DEFAULT_FEE;
  #private = false;
  /** Null until the account is known (`@/ui/afford`): nothing is refused for affordability meanwhile. */
  #balance: Balance = null;
  #busy = false;
  /** Free-entry tickets held, by wager. */
  #tickets: Record<string, number> = {};
  /** The player's own open challenges, by wager: "N yours" under the chip. */
  #openByFee: Record<string, number> = {};
  /** The fee the ticket was turned on at (see `ticketAskedAt`); null while it is off. */
  #ticketAt: number | null = null;
  /** The shortfall line is up (so it can be taken down when the balance recovers). */
  #brokeShown = false;
  readonly #ticket = ticketSwitch(() => {
    this.#ticketAt = this.#ticketAt === null ? this.#fee : null;
    this.#paint();
  });

  constructor(private readonly callbacks: CreateChallengeCallbacks) {
    const { screen, panel, fit } = createScreen('create', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', '21 Wild — create a challenge');


    // Inside the panel, not prepended to the screen. `.w21-screen` is a flex
    // ROW, so a second child of it lands beside the panel rather than above it.
    this.topBar = new TopBar(callbacks.bar ?? {
      onHistory: () => callbacks.onHistory(),
      onHome: () => callbacks.onHome(),
      onProfile: () => callbacks.onProfile(),
    });
    screen.append(this.topBar.root);

    // Blockari's page: a column holding one panel — head, lede, the cross link, the count, then the stack.
    panel.className = 'w21-page w21-centred';
    const sheet = el('div', 'w21-panel');

    const head = el('div', 'w21-panel-head');
    head.append(el('h2', undefined, 'Create Challenge'), el('span', 'w21-chip w21-amber', 'Set the bar'));

    const lede = el(
      'p',
      'w21-panel-lede',
      'Name your wager and play a fresh deck right now. The challenge then ' +
        'waits up to 24 hours for someone to take it — they play the exact same ' +
        'cards. Winner takes both wagers. Your name stays hidden until someone takes it.',
    );

    // Blockari's Switch (the same one Settings uses): a label, the track, the name and its hint.
    this.#privacy = kitSwitch('Private challenge', 'Only joinable with the share code; hidden from matchmaking.', false, (on) => {
      this.#private = on;
      this.#paint();
    });

    this.#confirm = el('button', 'w21-btn w21-warm w21-block w21-lg') as HTMLButtonElement;
    this.#confirm.type = 'button';
    this.#confirm.addEventListener('click', () => {
      if (this.#busy) return;
      this.#busy = true;
      this.#paint();
      this.callbacks.onCreate(this.#fee, this.#private, this.#withTicket());
    });

    const note = kitNote('Your game starts right away: three minutes on the clock, no pauses.');

    // Your own waiting challenges are not on the take screen (you cannot take them); they are counted here.

    this.#setUpAmount();
    const fee = el('div', 'w21-fee');
    this.#feeHint.setAttribute('aria-live', 'polite');
    this.#feeHint.hidden = true;
    fee.append(el('span', 'w21-field-label', 'Wager'), this.#wager.root, this.#feeHint, this.#pot);
    const stack = el('div', 'w21-stack');
    // Blockari's Length: a field label and a warm segmented control under the pot (and the ticket row), above Private.
    stack.append(fee, this.#ticket.root, this.#privacy.root, this.#error, this.#confirm, note);
    sheet.append(
      head,
      lede,
      crossLink('Rather take one?', 'Take a challenge', callbacks.bar?.onTake),
      this.#created.root,
      stack,
    );
    const column = el('div', 'w21-column');
    column.append(sheet);
    panel.append(column);
  }

  readonly #created = new CountLine('created', () => openOwnWagers((id) => this.callbacks.onOpenChallenge?.(id)));

  /** How many challenges you have created and how many still wait (0 hides the line). */
  setCounts(counts: ChallengeCounts | null): void {
    this.#created.set(counts);
    this.#openByFee = counts?.openByFee ?? {};
    this.#paint();
    this.#fit.measure();
  }

  /** The CUSTOM tile's input: digits only, 16px (no iOS zoom), Enter or blur commits when valid. */
  #setUpAmount(): void {
    const input = this.#amount;
    input.id = 'w21-fee-amount';
    input.type = 'text';
    input.inputMode = 'numeric';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.enterKeyHint = 'done';
    input.placeholder = 'Amount';
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('aria-label', 'Custom wager in $CHAIN');
    input.setAttribute('aria-describedby', 'w21-fee-hint');
    this.#feeHint.id = 'w21-fee-hint';
    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '');
      this.#text = digits;
      this.#fee = parseFeeText(digits);
      this.#error.textContent = '';
      this.#paint();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.#commit();
    });
    input.addEventListener('blur', () => this.#commit());
  }

  /** Enter or blur: a valid amount closes the input; nothing typed goes back to the last quick tile; an invalid one stays open. */
  #commit(): void {
    if (!this.#editing) return;
    if (this.#text === '') {
      this.#customSel = false;
      this.#editing = false;
      this.#fee = this.#lastChip;
    } else if (feeProblem(this.#fee, this.#bounds) === null) {
      this.#editing = false;
    }
    // An invalid entry keeps the tile an input (and the Stake button disabled).
    this.#paint();
  }

  /** The quick wager last picked (CUSTOM left empty goes back to it). */
  get #lastChip(): number {
    return this.#bounds.fees.includes(this.#chip) ? this.#chip : (this.#bounds.fees[0] ?? DEFAULT_FEE);
  }
  #chip = DEFAULT_FEE;

  /** A wager that is not a quick pick (the remembered one, a settled one) lives on the CUSTOM tile; one that is a quick pick does not. */
  #syncCustom(): void {
    const { fees } = this.#bounds;
    if (this.#fee > 0 && !fees.includes(this.#fee)) {
      this.#text = String(this.#fee);
      this.#customSel = true;
    } else if (!this.#editing && (this.#fee === 0 || this.#fee !== parseFeeText(this.#text))) {
      this.#customSel = false;
    }
  }

  /** The tickets the player holds, by wager (from the inventory). */
  setTickets(tickets: Record<string, number>): void {
    this.#tickets = tickets;
    this.#paint();
  }

  /** Whether this entry is paid with a ticket: asked for at this wager, and one is held at it. */
  #withTicket(): boolean {
    this.#ticketAt = ticketAskedAt(this.#ticketAt, this.#fee, this.#tickets[String(this.#fee)] ?? 0);
    return this.#ticketAt !== null;
  }

  /** Called whenever the screen is opened: the bounds and the balance may both have moved. */
  show(bounds: FeeBounds, balance: Balance): void {
    this.#bounds = bounds;
    this.#balance = balance;
    // Keep the player's last wager if the platform still allows it and they can afford it —
    // reopening the screen to find it reset is a small insult. Otherwise the dearest chip they
    // CAN afford, then the smallest allowed amount. (An unknown balance keeps it: see `setBalance`.)
    this.#ticketAt = null;
    this.#fee = settleFee(this.#fee, bounds.fees, bounds.min, balance, feeProblem(this.#fee, bounds) === null, this.#tickets[String(this.#fee)] ?? 0);
    this.#editing = false;
    this.#syncCustom();
    this.#busy = false;
    this.#error.textContent = '';
    this.#brokeShown = false;
    this.#paint();
  }

  /**
   * The balance changed, or became known: the same updates the top bars get. Re-evaluated in place (the wager, the
   * ladder's chips, the Create button, the shortfall line); nothing else about the screen resets. The first time it
   * is known a wager the player cannot afford is settled to one they can, as opening with it known would have.
   */
  setBalance(balance: Balance): void {
    const first = this.#balance === null && balance !== null;
    this.#balance = balance;
    if (first) {
      this.#fee = settleFee(this.#fee, this.#bounds.fees, this.#bounds.min, balance, feeProblem(this.#fee, this.#bounds) === null, this.#tickets[String(this.#fee)] ?? 0);
      this.#syncCustom();
    }
    this.#paint();
  }

  /** A create that failed — the stake is still theirs, and they should be told why. */
  fail(message: string): void {
    this.#busy = false;
    this.#error.textContent = CREATE_ERROR[message] ?? message;
    this.#paint();
  }

  #paint(): void {
    // One segmented control rather than a grid of cards: the stakes are a single ordered choice and reading them
    // as a row is what makes the ladder legible as a ladder. The fifth tile is CUSTOM (an input in its own cell).
    const fees = this.#bounds.fees;
    const problem = feeProblem(this.#fee, this.#bounds);
    if (fees.includes(this.#fee)) this.#chip = this.#fee;
    const typed = this.#customSel && this.#text !== '';
    this.#wager.paint({
      fees,
      selected: this.#customSel ? 'custom' : fees.includes(this.#fee) ? this.#fee : null,
      cell: (fee) => {
        const held = this.#tickets[String(fee)] ?? 0;
        // A held ticket pays the entry, so a tile is open to a player who could not afford it.
        const afford = canAfford(fee, this.#balance, held);
        const yours = this.#openByFee[String(fee)] ?? 0;
        const hint = createChipHint(yours, held > 0);
        const name = quickChipAriaLabel(fee, yours, held > 0);
        return {
          hint: hint.text,
          glyph: hint.glyph,
          disabled: !afford,
          ...(afford ? {} : { title: 'More than your balance' }),
          ...(name ? { ariaLabel: name } : {}),
        };
      },
      custom: {
        label: typed ? fmtAmount(parseFeeText(this.#text)) : 'Custom',
        // Your own open ones at amounts that are not a chip; once an amount is committed, the ones at that amount.
        hint: customChipHint(this.#openByFee, fees, typed ? parseFeeText(this.#text) : null),
        ...(() => {
          const name = chipAriaLabel(typed ? fmtAmount(parseFeeText(this.#text)) : 'Custom', customYours(this.#openByFee, fees, typed ? parseFeeText(this.#text) : null));
          return name ? { ariaLabel: name } : {};
        })(),
        editing: this.#editing,
      },
      disabled: this.#busy,
    });
    if (this.#amount.value !== this.#text) this.#amount.value = this.#text;
    this.#amount.maxLength = String(this.#bounds.max).length + 1;
    this.#amount.disabled = this.#busy;
    const shownProblem = typed && problem !== null;
    if (shownProblem) this.#amount.setAttribute('aria-invalid', 'true');
    else this.#amount.removeAttribute('aria-invalid');
    this.#feeHint.hidden = !shownProblem;
    this.#feeHint.textContent = shownProblem ? problem : '';

    const held = this.#tickets[String(this.#fee)] ?? 0;
    const ticketOn = this.#withTicket();
    this.#ticket.paint(problem === null ? held : 0, ticketOn, this.#fee);
    // The pot card in Take's (and Blockari's) markup: the coin, "Winner takes" over the amount, "Your stake" beside it.
    const mid = el('div');
    mid.append(el('div', 'w21-label', 'Winner takes'), el('div', 'w21-amount', problem === null ? `${formatScore(this.#fee * 2)} $CHAIN` : '\u2014'));
    const stakeBox = el('div', 'w21-stake', 'Your stake');
    stakeBox.append(el('b', undefined, problem !== null ? '\u2014' : ticketOn ? 'Ticket' : `${formatScore(this.#fee)} $CHAIN`));
    this.#pot.replaceChildren(coin(), mid, stakeBox);

    this.#privacy.input.checked = this.#private;
    this.#privacy.input.disabled = this.#busy;

    // Newly reachable now that nothing is free: a player can be too poor for
    // the cheapest rung. Saying so plainly, and when it changes, beats a
    // disabled button with no explanation.
    // Only with a KNOWN balance: while the account is still loading nothing is said and nothing is disabled.
    const broke = problem === null && isShort(this.#fee, this.#balance, ticketOn);
    if (broke && !this.#busy) {
      this.#error.textContent =
        `You need ${formatScore(this.#fee)} $CHAIN for this wager. You have ${formatScore(this.#balance ?? 0)} — your top-up lands every day, or play Practice for nothing.`;
      this.#brokeShown = true;
    } else if (!broke && this.#brokeShown) {
      // It recovered (a top-up, a win, the account arriving): the line goes with the shortfall.
      this.#error.textContent = '';
      this.#brokeShown = false;
    }

    this.#confirm.disabled = this.#busy || broke || problem !== null;
    this.#confirm.textContent = this.#busy
      ? 'Creating…'
      : problem !== null
        ? 'Set your wager'
        : ticketOn
          ? 'Play with ticket'
          : `Stake ${formatScore(this.#fee)} $CHAIN and play`;
    this.#fit.measure();
  }
}

export interface TakeChallengeCallbacks {
  /**
   * The shell bar's targets, wired once in `mountUI` and handed to every
   * screen, so the bar is the same bar on every route. Without this each
   * screen built its own from its own callbacks, and the History screen's had
   * nothing on it: balance and avatar no-ops, brand hidden, the only way out a
   * Back button under thirty cards.
   */
  bar?: TopBarCallbacks;
  /** Take any open challenge at this stake; `useTicket` pays it with a free-entry ticket. */
  onMatch(entryFee: number, useTicket: boolean): void;
  /** Take one specific challenge, by its share code. */
  onCode(code: string): void;
  /** The wager moved (a chip or a typed amount): the queue at that stake is worth asking for. */
  onFee?(entryFee: number): void;
  /** Nobody is waiting: set one up instead. */
  onCreate?(): void;
  onBack(): void;
  onHistory(): void;
  onHome(): void;
  onProfile(): void;
}

/** The share alphabet, minus the characters that are ambiguous in a code. */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;

/**
 * Take a challenge: match on a stake, or enter a code somebody sent.
 *
 * Both routes are on one screen because they are the same decision — "play
 * someone" — and splitting them makes the player choose a menu before choosing
 * a game. Matchmaking is listed first and shows live counts per tier, so an
 * empty tier is visibly empty rather than a button that fails.
 */
const ICON_SWORDS =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4l11 11"></path><path d="M20 4L9 15"></path><path d="M12.5 12.5L6 19M11.5 12.5L18 19"></path><path d="M4.5 16.5l3 3M19.5 16.5l-3 3"></path><path d="M5 21l2-2M19 21l-2-2"></path></svg>';
export const ICON_PASTE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="8" y="2" width="8" height="4" rx="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="M9 13h6M9 17h4"></path></svg>';

/**
 * Take a challenge — Blockari's `TakeChallenge`, in the kit's markup.
 *
 * Two panels: the picker (the fee ladder with per-rung counts, the pot,
 * one line about the queue at that fee, Match me, then the code field and
 * Join challenge under a labelled divider) and the open queue itself, one
 * row per challenge a take could hand out right now. Side by side from
 * 1000 px, stacked under it.
 */
export class TakeChallengeScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;

  readonly #fit: PanelFit;
  /** The CUSTOM tile filters Open now to the amounts that are not a quick pick; it never matches. */
  #customFilter = false;
  readonly #wager = new WagerRow({
    quick: (fee) => {
      if (this.#busy) return;
      this.#customFilter = false;
      this.#fee = fee;
      this.#paint();
      this.callbacks.onFee?.(fee);
    },
    custom: () => {
      if (this.#busy) return;
      this.#customFilter = !this.#customFilter;
      this.#paint();
      this.callbacks.onFee?.(this.#fee);
      // The list is below the picker on a phone: bring it into view when the filter turns on (and again when its own
      // rows land, which make the page taller).
      this.#scrollWanted = this.#customFilter;
      if (this.#customFilter) this.#scrollToList();
    },
  });
  #scrollWanted = false;
  #scrollToList(): void {
    this.#queuePanel.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }
  readonly #queuePanel = el('div', 'w21-panel');
  /** "Custom wagers · Show all ✕", over the list while the filter is on. */
  readonly #filterChip = el('button', 'w21-queue-filter') as HTMLButtonElement;
  readonly #potAmount = el('div', 'w21-amount');
  readonly #potStake = el('b');
  readonly #queueLine = el('div', 'w21-queue');
  readonly #match: HTMLButtonElement;
  readonly #slots: HTMLElement[] = [];
  readonly #codeSlots = el('div', 'w21-code-slots');
  readonly #input: HTMLInputElement;
  readonly #join: HTMLButtonElement;
  readonly #error = el('div', 'w21-error');
  readonly #errorText = el('span');
  readonly #short = el('div', 'w21-note w21-warm');
  readonly #waiting = el('span', 'w21-live', '0 waiting');
  readonly #list = el('div', 'w21-queue-list');
  /** The queue's rows by challenge id, so a refresh updates rather than rebuilds. */
  readonly #rows = new Map<string, { node: HTMLElement; take: HTMLButtonElement; when: HTMLElement }>();

  /** Free-entry tickets held, by wager. */
  #tickets: Record<string, number> = {};
  /** The fee the ticket was turned on at (see `ticketAskedAt`); null while it is off. */
  #ticketAt: number | null = null;
  readonly #ticket = ticketSwitch(() => {
    if (this.#busy) return;
    this.#ticketAt = this.#ticketAt === null ? this.#fee : null;
    this.#paint();
  });
  readonly #taken = new CountLine('taken');
  #bounds: FeeBounds = DEFAULT_BOUNDS;
  #queues: Record<string, QueueTier> = {};
  #open: OpenChallenge[] | null = null;
  #fee = DEFAULT_FEE;
  /** Null until the account is known (`@/ui/afford`): no shortfall is said and nothing is locked for affordability meanwhile. */
  #balance: Balance = null;
  #busy: 'match' | 'join' | string | null = null;

  constructor(private readonly callbacks: TakeChallengeCallbacks) {
    const { screen, panel, fit } = createScreen('take', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', '21 Wild — take a challenge');

    this.topBar = new TopBar(callbacks.bar ?? {
      onHistory: () => callbacks.onHistory(),
      onHome: () => callbacks.onHome(),
      onProfile: () => callbacks.onProfile(),
    });
    screen.append(this.topBar.root);

    panel.className = 'w21-page';
    const layout = el('div', 'w21-take-layout');
    const column = el('div', 'w21-column');
    const picker = el('div', 'w21-panel');

    /* -- the picker ---------------------------------------------------------- */
    const head = el('div', 'w21-panel-head');
    head.append(el('h2', undefined, 'Take Challenge'), el('span', 'w21-live', 'Live queue'));
    const lede = el(
      'p',
      'w21-panel-lede',
      'Pick a wager and you are matched with the oldest open challenge at that stake. You play their exact deck — same cards, same order. Beat their score to take the pot. Challengers stay anonymous until you take theirs.',
    );

    const stack = el('div', 'w21-stack');
    const feeWrap = el('div');
    const fee = el('div', 'w21-fee');
    const pot = el('div', 'w21-pot');
    pot.setAttribute('aria-live', 'polite');
    const potMid = el('div');
    potMid.append(el('div', 'w21-label', 'Winner takes'), this.#potAmount);
    const potStake = el('div', 'w21-stake', 'Your stake');
    potStake.append(this.#potStake);
    pot.append(coin(), potMid, potStake);
    fee.append(el('span', 'w21-field-label', 'Wager'), this.#wager.root, pot);
    feeWrap.append(fee);
    this.#queueLine.setAttribute('aria-live', 'polite');

    this.#match = el('button', 'w21-btn w21-block w21-lg w21-primary') as HTMLButtonElement;
    this.#match.type = 'button';
    this.#match.innerHTML = ICON_SWORDS;
    this.#match.append(document.createTextNode('Match me'));
    this.#match.addEventListener('click', () => {
      if (this.#busy || this.#match.disabled) return;
      this.#busy = 'match';
      this.#paint();
      this.callbacks.onMatch(this.#fee, this.#withTicket());
    });
    stack.append(feeWrap, this.#ticket.root, this.#short, this.#queueLine, this.#match);

    /* -- the code field ------------------------------------------------------ */
    // One real input behind six drawn slots. A field per character is the
    // usual way to build this and it is the reason those fields fight paste,
    // backspace and autofill on every platform — the browser only understands
    // one of them. This keeps the single input the browser already knows how
    // to drive and draws the boxes on top of it.
    const tight = el('div', 'w21-stack w21-tight');
    const field = el('div', 'w21-code-field');
    for (let i = 0; i < CODE_LENGTH; i++) {
      const slot = el('span', 'w21-slot w21-empty');
      slot.setAttribute('aria-hidden', 'true');
      this.#slots.push(slot);
      this.#codeSlots.append(slot);
    }
    this.#input = el('input') as HTMLInputElement;
    this.#input.type = 'text';
    this.#input.inputMode = 'text';
    this.#input.autocapitalize = 'characters';
    this.#input.autocomplete = 'off';
    this.#input.spellcheck = false;
    this.#input.maxLength = CODE_LENGTH;
    this.#input.setAttribute('autocorrect', 'off');
    this.#input.setAttribute('aria-label', 'Challenge code');
    this.#input.addEventListener('input', () => this.#onCode());
    this.#input.addEventListener('focus', () => this.#paint());
    this.#input.addEventListener('blur', () => this.#paint());
    this.#input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && this.#ready()) this.#submit();
    });
    this.#codeSlots.append(this.#input);
    this.#codeSlots.addEventListener('click', () => this.#input.focus());

    const paste = el('button', 'w21-btn w21-paste') as HTMLButtonElement;
    paste.type = 'button';
    paste.setAttribute('aria-label', 'Paste code');
    paste.title = 'Paste';
    paste.innerHTML = ICON_PASTE;
    paste.append(el('span', 'w21-lbl', window.isSecureContext ? 'Paste from clipboard' : 'Paste'));
    paste.addEventListener('click', () => {
      void navigator.clipboard
        ?.readText()
        .then((text) => {
          this.#input.value = codeIn(text);
          this.#onCode();
          this.#input.focus();
        })
        .catch(() => this.#input.focus());
    });
    // A pasted share link is a link, not a code: the code is lifted out of it.
    this.#input.addEventListener('paste', (event) => {
      const text = event.clipboardData?.getData('text') ?? '';
      if (!text) return;
      event.preventDefault();
      this.#input.value = codeIn(text);
      this.#onCode();
    });
    field.append(this.#codeSlots, paste);

    this.#join = el('button', 'w21-btn w21-block') as HTMLButtonElement;
    this.#join.type = 'button';
    this.#join.textContent = 'Join challenge';
    this.#join.addEventListener('click', () => this.#submit());
    // The error note, after Join, in words: the kit's `.error` with the info
    // glyph. A server code never reaches the player.
    this.#error.innerHTML = ICON_INFO;
    this.#error.append(this.#errorText);
    // Announced when it lands: the note appears under a button that was just
    // pressed, and a screen reader is otherwise still on the button.
    this.#error.setAttribute('role', 'alert');
    this.#error.hidden = true;
    // Short of $CHAIN for the chosen fee: said once, under the ladder's pot.
    this.#short.innerHTML = ICON_INFO;
    this.#short.append(el('span', undefined, 'Not enough $CHAIN for this wager. Pick a lower amount, or wait for the daily top-up.'));
    this.#short.hidden = true;
    tight.append(field, this.#join, this.#error);

    picker.append(head, lede, crossLink('Want to set the bar?', 'Create a challenge', callbacks.bar?.onCreate ?? callbacks.onCreate), this.#taken.root, stack, el('div', 'w21-divider w21-labelled', 'or join with a code'), tight);
    column.append(picker);

    /* -- the queue ----------------------------------------------------------- */
    const queue = this.#queuePanel;
    const queueHead = el('div', 'w21-panel-head');
    queueHead.append(el('h3', undefined, 'Open now'), this.#waiting);
    this.#filterChip.type = 'button';
    this.#filterChip.hidden = true;
    this.#filterChip.append('Custom wagers ', el('b', undefined, '\u00b7 Show all \u2715'));
    this.#filterChip.addEventListener('click', () => {
      this.#customFilter = false;
      this.#paint();
      this.callbacks.onFee?.(this.#fee);
    });
    queue.append(queueHead, this.#filterChip, this.#list);

    layout.append(column, queue);
    panel.append(layout);
  }

  show(bounds: FeeBounds, queues: Record<string, QueueTier>, balance: Balance): void {
    this.#bounds = bounds;
    this.#queues = queues;
    this.#balance = balance;
    this.#busy = null;
    this.#ticketAt = null;
    this.#error.hidden = true;
    // The chosen wager survives a refresh while the platform allows it; otherwise the
    // smallest chip with somebody waiting, or the first allowed amount.
    if (!bounds.fees.includes(this.#fee)) {
      this.#fee = bounds.fees.find((f) => (queues[String(f)]?.open ?? 0) > 0) ?? Math.min(Math.max(DEFAULT_FEE, bounds.min), bounds.max);
    }
    this.#paint();
  }

  /** The balance changed, or became known: the same updates the top bars get. Re-evaluated in place (Match me, the shortfall line, each row's Take). */
  setBalance(balance: Balance): void {
    this.#balance = balance;
    this.#paint();
  }

  /** The counts alone, from a poll: the ladder and the line, nothing else moves. */
  setQueues(queues: Record<string, QueueTier>): void {
    this.#queues = queues;
    this.#paint();
  }

  /** The tickets the player holds, by wager (from the inventory). */
  setTickets(tickets: Record<string, number>): void {
    this.#tickets = tickets;
    this.#paint();
  }

  /** How many challenges you have taken (the quiet line under the intro). */
  setCounts(counts: ChallengeCounts | null): void {
    this.#taken.set(counts);
    this.#fit.measure();
  }

  /** Whether Match me pays with a ticket: asked for at this wager, and one is held at it. */
  #withTicket(): boolean {
    this.#ticketAt = ticketAskedAt(this.#ticketAt, this.#fee, this.#tickets[String(this.#fee)] ?? 0);
    return this.#ticketAt !== null;
  }

  /** The chosen fee, for the poller that asks the server for that fee first. */
  get fee(): number {
    return this.#fee;
  }

  /** Whether the CUSTOM filter is on (and something custom is waiting): the poll asks for those amounts. */
  get customFilter(): boolean {
    return this.#customFilter && customCount(this.#queues, this.#bounds.fees) > 0;
  }

  /** Whether the code field has a full code — a poll must not clobber a Join in flight. */
  get busy(): boolean {
    return this.#busy !== null;
  }

  /** The open queue, as fetched; the chosen fee's rows come first. */
  setQueue(rows: OpenChallenge[]): void {
    this.#open = rows;
    this.#paint();
    if (this.#scrollWanted && this.#customFilter) {
      this.#scrollWanted = false;
      this.#scrollToList();
    }
  }

  /**
   * A take that did not happen, in the player's words.
   *
   * The API answers with a code; the note says what it means and what to do.
   * Raw codes never reach the page.
   */
  fail(code: string): void {
    this.#busy = null;
    this.#errorText.textContent = TAKE_ERROR[code] ?? 'That did not work. Try again.';
    this.#error.hidden = false;
    this.#paint();
  }

  /** Focus the code field — for arriving on a share link. */
  prefill(code: string): void {
    this.#input.value = code.toUpperCase().slice(0, CODE_LENGTH);
    this.#onCode();
  }

  #ready(): boolean {
    return !this.#busy && this.#input.value.length === CODE_LENGTH;
  }

  #submit(): void {
    if (!this.#ready()) return;
    this.#busy = 'join';
    this.#paint();
    this.callbacks.onCode(this.#input.value);
  }

  #onCode(): void {
    // Normalise as they type: uppercase, and drop anything not in the alphabet
    // so a pasted "MCY-8HX" or a stray space becomes the code it was meant to be.
    const cleaned = [...this.#input.value.toUpperCase()]
      .filter((ch) => CODE_ALPHABET.includes(ch))
      .join('')
      .slice(0, CODE_LENGTH);
    if (cleaned !== this.#input.value) this.#input.value = cleaned;
    this.#error.hidden = true;
    this.#paint();
  }

  #paint(): void {
    /* -- the wager row: four quick tiles and CUSTOM --------------------------- */
    const chips = this.#bounds.fees;
    // The filter ends when the last custom challenge goes.
    const customN = customCount(this.#queues, chips);
    const filterOn = this.#customFilter && customN > 0;
    this.#wager.paint({
      fees: chips,
      selected: filterOn ? 'custom' : chips.includes(this.#fee) ? this.#fee : null,
      // Who is waiting first; else a ticket held at this wager.
      cell: (fee) => ({ hint: takeChipHint(this.#queues[String(fee)]?.open ?? 0, (this.#tickets[String(fee)] ?? 0) > 0) }),
      custom: { label: 'Custom', hint: `${customN} open`, disabled: customN === 0 },
      disabled: this.#busy !== null,
    });
    this.#filterChip.hidden = !filterOn;

    const problem = feeProblem(this.#fee, this.#bounds);
    const ticketOn = this.#withTicket();
    this.#ticket.paint(problem === null ? (this.#tickets[String(this.#fee)] ?? 0) : 0, ticketOn, this.#fee);
    this.#potAmount.textContent = problem === null ? `${formatScore(this.#fee * 2)} $CHAIN` : '\u2014';
    this.#potStake.textContent = problem !== null ? '\u2014' : ticketOn ? 'Ticket' : `${formatScore(this.#fee)} $CHAIN`;

    /* -- the queue line ------------------------------------------------------ */
    // Counts and ages only: nobody is named before the take.
    const open = problem === null ? (this.#queues[String(this.#fee)]?.open ?? 0) : 0;
    this.#queueLine.replaceChildren();
    if (filterOn) {
      this.#queueLine.textContent = 'Showing custom wagers. Pick a quick wager to Match me.';
    } else if (problem !== null) {
      this.#queueLine.textContent = 'Pick a wager to see who is waiting.';
    } else if (open === 0) {
      this.#queueLine.textContent = 'Nobody is waiting at this stake right now.';
    } else {
      const oldest = (this.#open ?? []).filter((row) => row.entryFee === this.#fee)[0] ?? null;
      this.#queueLine.append(el('b', undefined, String(open)), document.createTextNode(` open at ${formatScore(this.#fee)} $CHAIN`));
      this.#queueLine.append(document.createTextNode(oldest ? ` \u2014 oldest posted ${timeAgo(oldest.createdAt)}.` : '.'));
    }

    /* -- the two buttons ----------------------------------------------------- */
    const typed = this.#input.value.length === CODE_LENGTH;
    const afford = problem !== null || ticketOn || canAfford(this.#fee, this.#balance);
    this.#short.hidden = afford;
    this.#match.classList.toggle('w21-primary', !typed);
    this.#match.disabled = this.#busy !== null || open === 0 || !afford || filterOn;
    this.#join.classList.toggle('w21-primary', typed);
    this.#join.disabled = !this.#ready();
    // Blockari's wording (TakeChallenge.tsx): with the ticket on, the button says so, as the stake line does.
    setBusy(this.#match, this.#busy === 'match', ICON_SWORDS, ticketOn ? 'Match me \u00b7 ticket' : 'Match me');
    setBusy(this.#join, this.#busy === 'join', '', 'Join challenge');

    const value = this.#input.value;
    const focused = document.activeElement === this.#input;
    this.#slots.forEach((slot, i) => {
      const filled = i < value.length;
      slot.textContent = filled ? (value[i] ?? '') : '';
      slot.className = `w21-slot ${filled ? 'w21-filled' : 'w21-empty'}${i === value.length && focused && !this.#busy ? ' w21-cursor' : ''}`;
    });

    /* -- the open queue ------------------------------------------------------ */
    // Before the first answer: three skeleton cards. Nobody waiting: say so. (Also while the CUSTOM filter waits for
    // its own list: something custom is open, the rows are on their way.)
    const everyone = Object.values(this.#queues).reduce((n, tier) => n + tier.open, 0);
    const rows = this.#open ? openRows(this.#open, chips, this.#fee, filterOn, 8) : [];
    if (this.#open === null || (filterOn && rows.length === 0)) {
      this.#waiting.textContent = this.#open === null ? 'Checking' : `${everyone} waiting`;
      this.#rows.clear();
      if (!this.#list.querySelector('.w21-skeleton')) {
        this.#list.replaceChildren(
          ...[0, 1, 2].map((i) => {
            const card = el('div', 'w21-skeleton w21-card');
            card.setAttribute('aria-hidden', 'true');
            card.style.animationDelay = `${i * 80}ms`;
            return card;
          }),
        );
      }
      this.#fit.measure();
      return;
    }
    // The stakes closest to the wager picked first, the oldest first among equals; under the CUSTOM filter only
    // the amounts that are not a quick pick, smallest first.
    this.#waiting.textContent = `${everyone || this.#open.length} waiting`;
    if (rows.length === 0) {
      this.#rows.clear();
      const empty = emptyBlock('Nobody waiting', 'Create a challenge and somebody will find it here.');
      if (this.callbacks.onCreate) {
        const create = el('button', 'w21-btn w21-primary w21-block', 'Create a challenge') as HTMLButtonElement;
        create.type = 'button';
        create.addEventListener('click', () => this.callbacks.onCreate?.());
        empty.append(create);
      }
      this.#list.replaceChildren(empty);
      this.#fit.measure();
      return;
    }
    // Reconciled by id: a poll that changes nothing moves nothing, and a row
    // that is already on the page keeps its place rather than re-entering.
    const seen = new Set<string>();
    rows.forEach((row, k) => {
      seen.add(row.id);
      const have = this.#rows.get(row.id);
      if (have) {
        have.node.style.setProperty('--k', String(k));
        have.when.textContent = this.#whenOf(row);
        have.take.disabled = this.#takeLocked(row);
        return;
      }
      this.#rows.set(row.id, this.#row(row, k));
    });
    for (const [id, have] of this.#rows) {
      if (!seen.has(id)) {
        have.node.remove();
        this.#rows.delete(id);
      }
    }
    const ordered = rows.map((row) => this.#rows.get(row.id)!.node);
    if (ordered.some((node, i) => this.#list.children[i] !== node) || this.#list.childElementCount !== ordered.length) {
      this.#list.replaceChildren(...ordered);
    }
    // More waiting than the page lists: say so (Blockari's `.queue-more`).
    this.#list.querySelector('.w21-queue-more')?.remove();
    if (!filterOn && everyone > rows.length) {
      this.#list.append(el('div', 'w21-queue-more', `Showing the ${rows.length} nearest your wager \u2014 pick another wager to find others.`));
    }
    this.#fit.measure();
  }

  #whenOf(row: OpenChallenge): string {
    return `Seed already played \u00b7 ${timeAgo(row.createdAt)}${row.code ? ` \u00b7 ${row.code}` : ''}`;
  }

  /** Take is off on your own row, while something is in flight, and when the stake is more than you hold. */
  #takeLocked(row: OpenChallenge): boolean {
    return this.#busy !== null || row.code === null || !canAfford(row.entryFee, this.#balance);
  }

  /** One row of the open queue: who set it, what it costs, and Take. */
  #row(row: OpenChallenge, k: number): { node: HTMLElement; take: HTMLButtonElement; when: HTMLElement } {
    const node = el('div', 'w21-queue-row');
    node.style.setProperty('--k', String(k));
    // A challenger nobody has taken yet: no initials, no colour of their own.
    const avatar = el('span', 'w21-avatar w21-anon', '?');
    avatar.setAttribute('aria-hidden', 'true');

    const body = el('div');
    body.style.minWidth = '0';
    const who = el('div', 'w21-who-name', 'Anonymous challenger');
    const sub = el('div', 'w21-who-sub');
    const money = el('div', 'w21-money-line');
    money.append(
      el('span', 'w21-stake', `Stake ${formatScore(row.entryFee)}`),
      document.createTextNode(' \u00b7 '),
      el('span', 'w21-pot-inline', `Pot ${formatScore(row.entryFee * 2)}`),
      el('span', 'w21-unit', ' $CHAIN'),
    );
    const when = el('div', undefined, this.#whenOf(row));
    sub.append(money, when);
    body.append(who, sub);

    const take = el('button', 'w21-btn w21-sm w21-primary', 'Take') as HTMLButtonElement;
    take.type = 'button';
    take.setAttribute('aria-label', `Take the ${formatScore(row.entryFee)} $CHAIN challenge`);
    take.disabled = this.#takeLocked(row);
    take.addEventListener('click', () => {
      if (take.disabled || !row.code) return;
      this.#busy = row.id;
      setBusy(take, true, '', 'Take');
      this.#paint();
      this.callbacks.onCode(row.code);
    });
    node.append(avatar, body, take);
    return { node, take, when };
  }
}

/** What a create's refusal means, in words. Keyed by the API's error codes; an unknown one is shown as it came. */
const CREATE_ERROR: Record<string, string> = {
  'insufficient-balance': 'Not enough $CHAIN for that wager.',
  'bad-fee': 'That wager is outside what is allowed. Pick another amount.',
  'ticket-not-for-fee': 'A free-entry ticket only covers a challenge at its own wager. Nothing was charged: turn the ticket off, or pick the wager it is for.',
  'not-signed-in': 'Sign in to create a challenge.',
};

export const ICON_INFO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v6M12 7v.5"></path></svg>';

/** What a take's refusal means, in words. Keyed by the API's error codes. */
const TAKE_ERROR: Record<string, string> = {
  'challenge-not-found': 'No challenge with that code.',
  'challenge-not-open': 'That challenge is not open yet — its creator has not played it, or it has been taken.',
  'challenge-expired': 'That challenge ran out of time.',
  'own-challenge': 'That one is yours. Send the code to somebody else.',
  'insufficient-balance': 'Not enough $CHAIN for that stake.',
  'no-open-challenge': 'Nobody is waiting at this stake right now.',
  'bad-fee': 'That wager is outside what is allowed. Pick another amount.',
  'ticket-not-for-fee': 'A free-entry ticket only covers a challenge at its own wager. Nothing was charged: turn the ticket off, or pick the wager it is for.',
  'bad-code': 'A code is six letters and numbers.',
  'not-signed-in': 'Sign in to take a challenge.',
  // Client-side, not the API's: the table was not up (nothing was taken), or a taken round did not start.
  'table-loading': 'The table is still loading, so nothing has been taken. Reload the page and try again.',
  'round-not-started': 'Your game did not start. Your seat is paid — it is waiting on its challenge page: press Play there.',
  'round-already-played': 'You have already played that deck, so it cannot be opened again.',
  'round-finished': 'That challenge is already finished, so your game could not be opened.',
  'round-not-seated': 'Take this challenge first: you have no seat on it yet. Nothing was charged.',
  'round-closed': 'That challenge has closed (it ran out of time or was already decided), so a game cannot be started on it. Nothing was charged.',
};

/**
 * The code in a pasted string: a share link's `?code=` (or the older `?c=`),
 * or the first six code characters of anything else. Pasting a whole URL
 * used to feed the field "HTTP27" — the letters of "http" and the digits of
 * the port.
 */
export function codeIn(text: string): string {
  const link = /[?&#](?:code|c)=([A-Za-z0-9]{6})\b/.exec(text);
  const raw = link?.[1] ?? text;
  return [...raw.toUpperCase()].filter((ch) => CODE_ALPHABET.includes(ch)).join('').slice(0, CODE_LENGTH);
}

/** A button in flight: the spinner before its label, and no second press. */
function setBusy(button: HTMLButtonElement, busy: boolean, icon: string, label: string): void {
  button.innerHTML = busy ? '<span class="w21-spinner w21-inline w21-dark" aria-hidden="true"></span>' : icon;
  button.append(document.createTextNode(label));
  button.setAttribute('aria-busy', String(busy));
}

/** The small XP chip on a player tag: "XP LV n" in the rank's colour. */
export function kitXpChipSm(level: number, rank: RankView): HTMLElement {
  const chip = el('span', `w21-chip w21-xp ${rankClass(rank.index)} w21-sm`);
  chip.title = `XP level ${level} \u00b7 ${rank.name} ${tierNumeral(rank.tier)}`;
  const b = el('b');
  b.append(odometer(level));
  chip.append(el('small', undefined, 'XP'), el('span', 'w21-lv', 'LV'), b);
  return chip;
}

export interface ChallengeScreenCallbacks {
  /**
   * The shell bar's targets, wired once in `mountUI` and handed to every
   * screen, so the bar is the same bar on every route. Without this each
   * screen built its own from its own callbacks, and the History screen's had
   * nothing on it: balance and avatar no-ops, brand hidden, the only way out a
   * Back button under thirty cards.
   */
  bar?: TopBarCallbacks;
  onPlay(): void;
  onHistory(): void;
  onHome(): void;
  /** A profile: the viewer's own without a name, the opponent's with one. */
  onProfile(username?: string): void;
  /** Share the settled challenge as "Challenge Me" (the card's QR lands on the viewer's profile). */
  onShare?(view: ChallengeView): void;
}

/** What a challenge is doing, in the kit's words. */
type ChallengeState = 'won' | 'lost' | 'draw' | 'expired' | 'voided' | 'pending' | 'taken' | 'open';

/**
 * One challenge, from the moment it is dealt to the moment it pays out —
 * Blockari's `ChallengeDetail`, in the kit's markup.
 *
 * Deliberately a single screen rather than a waiting room and a results page.
 * It is the same object throughout — same stake, same pot, same opponent slot —
 * and the only thing that changes is how much of it is known. Two screens would
 * mean the player watching one of them navigate away from under them the moment
 * their opponent finished.
 *
 * `.panel-head` (label, code, status) → `.meta` (four facts) → `.vs` (me · VS ·
 * them) → `.stack`, in the kit's order: outcome · expired note · Play button ·
 * push prompt · share block · "playing now" note · nav row.
 */
export class ChallengeScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;

  readonly #fit: PanelFit;
  readonly #status = el('span', 'w21-status');
  readonly #code = el('h2');
  readonly #meta = el('div', 'w21-meta');
  readonly #vs = el('div', 'w21-vs');
  readonly #stack = el('div', 'w21-stack');
  readonly #push = new PushPrompt();

  #view: ChallengeView | null = null;
  #notice: string | null = null;
  /** The signed-in player's name — their own side is named, as Blockari names it. */
  #viewerName: string | null = null;

  constructor(private readonly callbacks: ChallengeScreenCallbacks) {
    const { screen, panel, fit } = createScreen('challenge', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.root.setAttribute('aria-label', '21 Wild — challenge');

    this.topBar = new TopBar(callbacks.bar ?? {
      onHistory: () => callbacks.onHistory(),
      onHome: () => callbacks.onHome(),
      onProfile: () => callbacks.onProfile(),
    });
    screen.append(this.topBar.root);

    // The kit's centred page: the card sits in the middle of what is left
    // under the bar, and top-aligns once it is taller than that.
    panel.className = 'w21-page w21-centred';
    const column = el('div', 'w21-column');
    const card = el('div', 'w21-panel');

    const head = el('div', 'w21-panel-head');
    const title = el('div');
    this.#code.style.letterSpacing = '0.18em';
    title.append(el('div', 'w21-t-label', 'Challenge'), this.#code);
    head.append(title, this.#status);

    card.append(head, this.#meta, this.#vs, this.#stack);
    column.append(card);
    panel.append(column);
  }

  /** Capture seam: force the prompt into one of its variants. See __w21notify. */
  showPushPrompt(variant: string): void {
    this.#push.force(variant);
  }

  /** Who is looking: their side carries their username rather than "You". */
  setViewer(name: string | null): void {
    this.#viewerName = name;
    if (this.#view) this.show(this.#view);
  }

  /** A line above the Play button — why the last attempt to play this seat did not start. Null clears it. */
  setNotice(text: string | null): void {
    this.#notice = text;
    if (this.#view) this.show(this.#view);
  }

  show(view: ChallengeView): void {
    this.#view = view;
    const state = this.#state(view);
    this.root.dataset.state = state;

    // Only the creator of a challenge still waiting for its second seat, and
    // only while there is an offer to make — the card's own gate covers the
    // rest. Never on won, lost, a taker's turn or a match in play: an offer
    // to be told about something later is behind the player by then.
    void this.#push.offer(
      view.role === 'creator' && (state === 'open' || state === 'pending'),
    );

    const label =
      state === 'lost' && view.myForfeited
        ? { text: 'LOST · TIMED OUT', cls: 'lost' }
        : state === 'won' && view.opponentForfeited
          ? { text: 'WON · FORFEIT', cls: 'won' }
          : STATUS_LABEL[state];
    this.#status.className = `w21-status${label.cls ? ` w21-${label.cls}` : ''}`;
    this.#status.textContent = label.text;

    // Only a real code earns the headline slot. An older challenge has none.
    this.#code.textContent = view.code ?? 'Challenge';

    const fee = `${formatScore(view.entryFee)} $CHAIN`;
    this.#meta.replaceChildren(
      metaCell('Stake', view.entryFee === 0 ? 'Free' : fee),
      metaCell('Pot', view.entryFee === 0 ? 'Bragging rights' : `${formatScore(view.entryFee * 2)} $CHAIN`),
      metaCell('Visibility', view.isPrivate ? 'Private' : 'Public'),
      metaCell(view.role === 'creator' ? 'Created' : 'Taken', timeAgo(view.createdAt)),
    );

    /* -- the VS block --------------------------------------------------------- */
    const decided = state === 'won' || state === 'lost';
    const meCls = decided ? (state === 'won' ? ' w21-win' : ' w21-lose') : '';
    const oppCls = decided ? (state === 'won' ? ' w21-lose' : ' w21-win') : '';
    const mine = view.me?.score ?? null;
    const theirs = view.opponent?.finished ? (view.opponent.score ?? null) : null;
    const top = Math.max(mine ?? 0, theirs ?? 0, 1);

    const me = el('div', `w21-side${meCls}`);
    if (view.me) {
      me.append(
        vsTag(view.me.rank, this.#viewerName ?? 'You', () => this.callbacks.onProfile()),
        rankLine(view.me.rank, view.me.xpLevel),
        el('div', 'w21-score', view.myForfeited ? '\u2014' : formatScore(view.me.score)),
      );
      if (!view.myForfeited) {
        if (view.me.level) me.append(matchLevelChip(view.me.level));
        me.append(vsBar((view.me.score / top) * 100));
      }
    } else if (state === 'voided' && view.voidedSeats) {
      // A seat that never produced a result still has its face: identity block, no score.
      me.append(
        vsTag(view.voidedSeats.me.rank, this.#viewerName ?? 'You', () => this.callbacks.onProfile()),
        rankLine(view.voidedSeats.me.rank, view.voidedSeats.me.xpLevel),
        el('div', 'w21-score', '\u2014'),
        el('span', 'w21-chip', 'No result'),
      );
    } else {
      me.append(el('div', 'w21-name', 'you'), el('div', 'w21-score', '\u2014'));
    }

    const them = el('div', `w21-side${oppCls}`);
    const opp = view.opponent;
    if (opp) {
      const rank = isNamed(opp) ? opp.rank : { index: UNRANKED, name: 'Unranked', tier: 1 };
      // A named opponent's tag opens their profile; an anonymous one has none.
      them.append(
        vsTag(rank, opp.name, () => {
          if (isNamed(opp)) this.callbacks.onProfile(opp.name);
        }),
        rankLine(rank, opp.xpLevel),
      );
      if (view.opponentForfeited) {
        // They never played it: nothing to compare against.
        them.append(el('div', 'w21-score', '\u2014'));
      } else if (theirs !== null) {
        them.append(el('div', 'w21-score', formatScore(theirs)));
        if (opp.level) them.append(matchLevelChip(opp.level));
        them.append(vsBar((theirs / top) * 100));
      } else {
        // Seated and still playing, or their score withheld until this
        // player's is in: an ellipsis, not a dash — the seat is not empty.
        // A voided challenge is nobody's to play any more: a dash.
        them.append(el('div', 'w21-score', state === 'voided' ? '\u2014' : '\u2026'));
      }
    } else if (state === 'voided' && view.voidedSeats?.opponent) {
      const seat = view.voidedSeats.opponent;
      them.append(
        vsTag(seat.rank, seat.name, () => this.callbacks.onProfile(seat.name)),
        rankLine(seat.rank, seat.xpLevel),
        el('div', 'w21-score', '\u2014'),
        el('span', 'w21-chip', 'No result'),
      );
    } else {
      them.append(el('div', 'w21-name', state === 'voided' ? 'no result' : 'waiting'), el('div', 'w21-score', '\u2014'));
    }

    this.#vs.replaceChildren(me, el('div', 'w21-mid', 'VS'), them);

    /* -- the stack, in the kit's order ----------------------------------------- */
    const stack: HTMLElement[] = [];

    if (decided || state === 'draw') stack.push(this.#outcome(view, state));
    if (state === 'expired') {
      stack.push(kitNote('Nobody took this challenge in time. Your fee was refunded.'));
    }
    if (state === 'voided') {
      stack.push(
        kitNote(
          view.voided === 'session-lost'
            ? 'This challenge was voided: a round was lost when the server restarted, so nobody could be ranked. Your stake was refunded to both players.'
            : view.voided === 'unpaid-seat'
              ? 'This challenge was voided: a seat on it was never paid for, so nobody was ranked. Any stake that was paid was refunded.'
              : 'This challenge was voided: neither seat was played. Both stakes were refunded.',
        ),
      );
    }
    if (state === 'pending') {
      if (this.#notice) {
        const why = el('p', 'w21-form__error', this.#notice);
        why.setAttribute('role', 'alert');
        stack.push(why);
      }
      const play = el('a', 'w21-btn w21-warm w21-block w21-lg', 'Play your game') as HTMLAnchorElement;
      play.href = '#play';
      play.addEventListener('click', (event) => {
        event.preventDefault();
        this.callbacks.onPlay();
      });
      stack.push(play);
    }
    // The push prompt sits BEFORE the share block, and is in the stack only
    // for the creator of a challenge still waiting for its second seat — the
    // card's own state decides the rest and hides itself when there is no
    // offer to make. Blockari mounts nothing otherwise, and neither does this.
    if (view.role === 'creator' && (state === 'open' || state === 'pending')) stack.push(this.#push.root);
    if ((state === 'open' || state === 'pending') && view.role === 'creator' && view.seats < 2 && view.code) {
      stack.push(...this.#share(view));
    }
    // A settled match the viewer played: brag it. Primary on a win, plain on a loss; the card's QR invites the
    // next challenge and lands on the viewer's profile — never on an open challenge, which stays anonymous.
    if (shareable(view) && this.callbacks.onShare) stack.push(this.#shareButton(view));
    if (state === 'taken' && view.me) {
      stack.push(
        kitNote(`${opp?.name ?? 'Your opponent'} is playing now. Results appear here when they finish.`, true),
      );
    }

    const nav = el('div', 'w21-nav-row');
    nav.append(
      kitButton('History', 'w21-btn w21-ghost w21-sm', () => this.callbacks.onHistory()),
      kitButton('Home', 'w21-btn w21-ghost w21-sm', () => this.callbacks.onHome()),
    );
    stack.push(nav);

    this.#stack.replaceChildren(...stack);
    this.#fit.measure();
    this.#fitPayfig();
  }

  /**
   * The payout figure at its set size unless it is wider than the banner it sits in (a very large prize on a narrow
   * phone), when it scales down to fit. Measured once the screen has its width, and again if the viewport changes.
   */
  #fitPayfig(): void {
    const fig = this.#stack.querySelector<HTMLElement>('.w21-payfig');
    if (!fig) return;
    const fit = (): void => {
      fig.style.fontSize = '';
      const box = fig.parentElement;
      if (!box || box.clientWidth === 0) return;
      const cs = getComputedStyle(box);
      const room = box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      // Layout pixels (offsetWidth), not painted ones: the panel may be scaled by its fit.
      const width = fig.offsetWidth;
      if (width > room && room > 0) fig.style.fontSize = `${Math.max(0.6, room / width).toFixed(3)}em`;
    };
    requestAnimationFrame(fit);
  }

  #state(view: ChallengeView): ChallengeState {
    // Voided first: the creator's round exists but nothing was decided, and the taker never got a result.
    if (view.voided) return 'voided';
    if (view.result) return view.result.won ? 'won' : 'lost';
    // Then the scores, which exist whether or not anything was staked. This
    // used to fall straight through to "in play", so every finished challenge
    // from before staking — both rounds played, nothing to settle — sat on
    // "waiting for them to finish" permanently.
    if (view.outcome) return view.outcome;
    // Expired: the deadline passed with the second seat still empty. The
    // creator's own round, played or not, is not taken away by the clock —
    // but nobody can take the challenge now, and the page says so rather
    // than WAITING for a challenger who cannot come.
    const expires = view.expiresAt ? Date.parse(view.expiresAt) : NaN;
    if (view.seats < 2 && Number.isFinite(expires) && expires < Date.now()) return 'expired';
    if (view.me === null) return 'pending';
    return view.seats >= 2 ? 'taken' : 'open';
  }

  /** "You won by 1746 points. +200 $CHAIN" and the levels, on two lines. */
  #outcome(view: ChallengeView, state: ChallengeState): HTMLElement {
    const box = el('div', `w21-outcome${state === 'won' ? ' w21-won' : state === 'lost' ? ' w21-lost' : ''}`);
    const mine = view.me?.score ?? null;
    const theirs = view.opponent?.score ?? null;
    let margin: string;
    if (state === 'lost' && view.myForfeited) {
      margin = 'You lost — you took this challenge but never played it, so it was forfeited.';
    } else if (state === 'won' && view.opponentForfeited) {
      margin = 'You won — your opponent took this challenge but never played it.';
    } else if (state === 'draw') {
      margin = 'Dead heat — scores tied.';
    } else {
      const who = state === 'won' ? 'You won' : 'You lost';
      const by = mine !== null && theirs !== null ? Math.abs(mine - theirs) : 0;
      if (by > 0) margin = `${who} by ${formatScore(by)} points.`;
      else {
        // Same score: the clock decided, and the player is told by how much.
        const a = view.me?.elapsedMs ?? null;
        const b = view.opponent?.elapsedMs ?? null;
        margin =
          a !== null && b !== null
            ? `${who} on the clock — scores tied, ${(Math.abs(a - b) / 1000).toFixed(1)}s apart.`
            : `${who} on submission time — scores and clocks were identical.`;
      }
    }
    box.append(document.createTextNode(margin));
    if (state === 'won' && view.result && view.result.payout > 0) {
      // The figure a win's coins leave from — the same words, in a span the flight can point at.
      // "+N $CHAIN" is one unit: it never breaks between the figure and the unit (a 360 banner orphaned "$CHAIN"),
      // and shrinks to the banner's width if it is wider than the line (`#fitPayfig`). The space before it is
      // outside the span, so the line may still break there.
      const fig = el('span', 'w21-payfig', `+${formatScore(view.result.payout)} $CHAIN`);
      fig.style.whiteSpace = 'nowrap';
      fig.dataset.payout = String(view.result.payout);
      box.append(document.createTextNode(' '), fig);
    }
    const myLevel = view.me?.level ?? null;
    const theirLevel = view.opponent?.level ?? null;
    if (myLevel !== null && theirLevel !== null && !view.myForfeited && !view.opponentForfeited) {
      // The two halves wrap as units: a narrow banner breaks before "they", never inside it (no lone "6.").
      const theirLine = el('span', undefined, `they reached level ${theirLevel}.`);
      theirLine.style.whiteSpace = 'nowrap';
      box.append(el('br'), document.createTextNode(`You reached level ${myLevel} \u00b7 `), theirLine);
    }
    return box;
  }

  #sharing = false;
  #shareBtn: HTMLButtonElement | null = null;

  /** The settled screen's Share button; a spinner and "Sharing" while the cards render and upload. */
  #shareButton(view: ChallengeView): HTMLButtonElement {
    const button = el('button', `w21-btn ${shareIsPrimary(view) ? 'w21-primary ' : ''}w21-block`) as HTMLButtonElement;
    button.type = 'button';
    button.dataset.share = 'challenge-me';
    this.#shareBtn = button;
    this.#paintShare();
    button.addEventListener('click', () => {
      if (this.#sharing) return;
      this.callbacks.onShare?.(view);
    });
    return button;
  }

  #paintShare(): void {
    const button = this.#shareBtn;
    if (!button) return;
    button.innerHTML = this.#sharing ? '<span class="w21-spinner w21-inline" aria-hidden="true"></span>' : ICON_SHARE_GLYPH;
    button.append(document.createTextNode(this.#sharing ? 'Sharing' : 'Share'));
    button.setAttribute('aria-busy', String(this.#sharing));
    button.disabled = this.#sharing;
  }

  /** The Share button while the cards render and upload. */
  setShareBusy(busy: boolean): void {
    this.#sharing = busy;
    this.#paintShare();
  }

  /** The share block: a labelled divider, the lead, the code, and two copies. */
  #share(view: ChallengeView): HTMLElement[] {
    const code = view.code ?? '';
    const divider = el('div', 'w21-divider w21-labelled', 'Share');
    const lead = el(
      'p',
      'w21-muted',
      `Waiting for a challenger. Send this code${view.isPrivate ? '' : ', or let matchmaking find one'}:`,
    );
    // Blockari's line between the lead and the code: who can see whom, and when.
    const hidden = kitNote('Your name stays hidden until someone takes the challenge.');
    const box = el('div', 'w21-code', code);
    const row = el('div', 'w21-row w21-split');
    const link = `${location.origin}${import.meta.env.BASE_URL}?code=${code}`;
    row.append(this.#copy('Copy link', link), this.#copy('Copy code', code));
    return [divider, lead, hidden, box, row];
  }

  /**
   * A copy button that says whether it worked.
   *
   * `navigator.clipboard` is unavailable on an insecure origin and can be
   * refused even on a secure one, so the failure is reported rather than
   * silently swallowed — a button that appears to have copied nothing is worse
   * than one that admits it.
   */
  #copy(label: string, value: string): HTMLButtonElement {
    const button = el('button', 'w21-btn') as HTMLButtonElement;
    button.type = 'button';
    const icon =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2"></rect><path d="M5 15V5a2 2 0 0 1 2-2h10"></path></svg>';
    const paint = (text: string): void => {
      button.innerHTML = icon;
      button.append(document.createTextNode(text));
    };
    paint(label);
    button.addEventListener('click', () => {
      void navigator.clipboard
        ?.writeText(value)
        .then(() => {
          paint('Copied');
          button.dataset.done = 'true';
        })
        .catch(() => paint("Couldn't copy"))
        .finally(() => {
          window.setTimeout(() => {
            paint(label);
            delete button.dataset.done;
          }, 1800);
        });
    });
    return button;
  }

  /** The challenge currently on screen, for a caller that needs to re-poll it. */
  get challengeId(): string | null {
    return this.#view?.id ?? null;
  }
}

export const ICON_SHARE_GLYPH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"></path><path d="M8 7l4-4 4 4"></path><path d="M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6"></path></svg>';

/** Blockari's `statusLabel`, plus the tie 21 Wild can produce. */
const STATUS_LABEL: Record<ChallengeState, { text: string; cls: string }> = {
  won: { text: 'WON', cls: 'won' },
  lost: { text: 'LOST', cls: 'lost' },
  draw: { text: 'TIED', cls: '' },
  expired: { text: 'EXPIRED', cls: '' },
  voided: { text: 'VOIDED · REFUNDED', cls: '' },
  pending: { text: 'YOUR TURN', cls: 'pending' },
  taken: { text: 'OPPONENT PLAYING', cls: 'taken' },
  open: { text: 'WAITING', cls: 'open' },
};

/** `Stake <b>100 $CHAIN</b>` — one cell of the meta grid. */
function metaCell(label: string, value: string): HTMLElement {
  const cell = el('span', undefined, `${label} `);
  cell.append(el('b', undefined, value));
  return cell;
}

/** The large tag on a VS side: the 40 px badge over the name. */
function vsTag(rank: RankView, name: string, onOpen: () => void): HTMLElement {
  const tag = el('a', `w21-player-tag w21-lg ${rankClass(rank.index)}`) as HTMLAnchorElement;
  tag.href = '#profile';
  tag.addEventListener('click', (event) => {
    event.preventDefault();
    onOpen();
  });
  tag.append(rankBadgeCard(rank, 40), el('span', 'w21-pt-name', name));
  return tag;
}

/** "MASON I · XP LV 11" under the name; the two stack on a phone. */
function rankLine(rank: RankView, xpLevel: number): HTMLElement {
  const line = el('div', `w21-rank-line ${rankClass(rank.index)}`);
  const xp = el('span', 'w21-xp-lv', 'XP LV ');
  xp.append(el('b', undefined, String(xpLevel)));
  line.append(
    el('span', 'w21-rank-name', `${rank.name} ${tierNumeral(rank.tier)}`),
    el('span', 'w21-sep', '\u00b7'),
    xp,
  );
  return line;
}

/** The thin bar under a score: this score against the higher of the two. */
function vsBar(percent: number): HTMLElement {
  const bar = el('div', 'w21-bar');
  const fill = el('i');
  fill.style.setProperty('--w', `${percent}%`);
  bar.append(fill);
  return bar;
}

/** The kit's `Note`: the info glyph and a line, warm when it is about a wait. */
function kitNote(text: string, warm = false): HTMLElement {
  const note = el('div', warm ? 'w21-note w21-warm' : 'w21-note');
  note.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v6M12 7v.5"></path></svg>';
  note.append(el('span', undefined, text));
  return note;
}

export interface ProfileCallbacks {
  /**
   * The shell bar's targets, wired once in `mountUI` and handed to every
   * screen, so the bar is the same bar on every route. Without this each
   * screen built its own from its own callbacks, and the History screen's had
   * nothing on it: balance and avatar no-ops, brand hidden, the only way out a
   * Back button under thirty cards.
   */
  bar?: TopBarCallbacks;
  onBack(): void;
  /** Open the create screen, to challenge the player being viewed. */
  onChallenge(): void;
  /** Open the take screen. */
  onTake(): void;
  /** The best-score tile: this player's top rounds. */
  onBestScores?(): void;
  /** The best-streak tile, on anyone's page: their streak counts (`GET /api/users/:username/streaks`). Absent: the tile is plain. */
  streaks?(username: string): Promise<Result<TopStreaks>>;
  /** The Rewards tab's data and its equip action (absent while chests are off). */
  rewards?: RewardsApi;
  /** "How it works": the full guide. */
  onGuide?(): void;
  /** Your own page's Settings button. */
  onSettings?(): void;
  /** Your own money card's "Free tokens" (the weekly faucet). Absent while the faucet is off: no button, and never on another player's page. */
  onFaucet?(): void;
  onHistory(): void;
  onHome(): void;
  onProfile(): void;
  /** A guest choosing to keep their rank. */
  onCreateAccount(): void;
  /** Open another player's profile (the opponent's tag on a Recent games card). */
  onOpenProfile?(username: string): void;
  /** Another player's settled games, a page at a time (`GET /api/users/:username/games`). Absent: no Recent games. */
  games?(username: string, cursor?: string | null): Promise<Result<GamesPage>>;
}

/**
 * A player's profile: rank, progress, and what they have to show for it.
 *
 * The shared Chain Games layout — hero card with the badge and the XP bar, a
 * grid of totals, then the guest note. Same order and same furniture as the
 * other titles, in 21 Wild's palette, so a player crossing between them is
 * reading the same page about themselves.
 *
 * Every figure here is the server's. Nothing is computed from what this client
 * happens to remember, because a profile that disagreed with the account it
 * describes would be worse than no profile.
 */
export class ProfileScreen {
  readonly root: HTMLElement;
  readonly topBar: TopBar;

  /** The viewer's last few challenges, shown on their own page only. */
  #recent: ChallengeCard[] = [];

  readonly #fit: PanelFit;
  /** The kit's `.profile` grid: hero left, recent challenges right. */
  readonly #profile = el('div', 'w21-profile');

  /**
   * The own money card's last row, "Free tokens" (Blockari's `.ledger-foot`): one element for the screen's life, put
   * back on whichever card is current (a refresh swaps the card), so the button keeps its state. Null while the
   * faucet is off.
   */
  readonly #faucetFoot: HTMLElement | null;
  readonly #faucet: FaucetEntry | null;

  constructor(private readonly callbacks: ProfileCallbacks) {
    const { screen, panel, fit } = createScreen('profile', true, 'main');
    this.root = screen;
    this.#fit = fit;
    this.#faucet = callbacks.onFaucet ? faucetEntry(() => callbacks.onFaucet?.()) : null;
    this.#faucetFoot = this.#faucet ? el('div', 'w21-ledger-foot') : null;
    if (this.#faucet && this.#faucetFoot) this.#faucetFoot.append(this.#faucet.root);
    this.root.setAttribute('aria-label', '21 Wild — profile');

    this.topBar = new TopBar(callbacks.bar ?? {
      onHistory: () => callbacks.onHistory(),
      onHome: () => callbacks.onHome(),
      onProfile: () => callbacks.onProfile(),
    });
    screen.append(this.topBar.root);

    // The kit's page, not a glass sheet with a Back button under it: the
    // page scrolls and the bar is the way out.
    panel.className = 'w21-page';
    // Stats | Rewards live inside the hero, on your own page when chests are on (built per show()).
    this.#rewardsHost = el('div', 'w21-rewards');
    this.#rewardsHost.hidden = true;
    panel.append(this.#profile);
  }

  /** The money card with its "Free tokens" row, when the faucet is on. */
  #withFaucet(card: HTMLElement): HTMLElement {
    if (this.#faucetFoot) card.append(this.#faucetFoot);
    return card;
  }

  #rewardsHost!: HTMLElement;
  /** The stats under the tabs (the money card and the tiles); hidden while the Rewards tab is up. */
  #statsBody: HTMLElement = el('div', 'w21-profile-body');
  #tabSeg: SegmentedControl | null = null;

  #selectTab(tab: 'stats' | 'rewards'): void {
    this.#tabSeg?.set(tab);
    this.#statsBody.hidden = tab !== 'stats';
    this.#rewardsHost.hidden = tab !== 'rewards';
    if (tab === 'rewards' && this.callbacks.rewards) mountRewards(this.#rewardsHost, this.callbacks.rewards);
    this.#fit.measure();
  }

  /** The rows to list under a player's own profile. */
  setRecent(rows: ChallengeCard[]): void {
    this.#recent = rows;
  }

  /** The "Recent challenges" panel on the viewer's own page, while that page is the one built. */
  #recentSide: HTMLElement | null = null;

  /** True while the page up is the viewer's own, with its Recent challenges panel in the document. */
  get showingOwn(): boolean {
    return this.root.dataset.mine === 'true' && this.#recentSide?.isConnected === true;
  }

  /** Whether the profile on screen counts any games (what decides if the panel lists rows or says "No challenges yet"). */
  #hasGames = false;
  /** The "No challenges yet" block, while the panel is showing it. */
  #recentEmpty: HTMLElement | null = null;

  /**
   * Newer rows for the Recent challenges panel, in place: nothing blanked, the scroll where it was, only the rows
   * that differ touched.
   */
  refreshRecent(rows: ChallengeCard[]): void {
    this.#recent = rows;
    this.#syncRecent();
  }

  /** Bring the panel to `#recent`: patch the list that is there, or swap the empty block for it once there is something to list. */
  #syncRecent(): void {
    const side = this.#recentSide;
    const rows = this.#hasGames ? this.#recent.slice(0, 6) : [];
    if (!side?.isConnected || rows.length === 0) return;
    const top = this.root.scrollTop;
    const next = matchRows(rows, () => this.callbacks.onHistory(), { code: false });
    const list = side.querySelector<HTMLElement>('.w21-glist');
    if (list) patchRows(list, next);
    else if (this.#recentEmpty) {
      for (const row of Array.from(next.children)) row.classList.add('w21-still');
      this.#recentEmpty.replaceWith(next);
    } else return;
    this.#recentEmpty = null;
    this.#fit.measure();
    this.root.scrollTop = top;
  }

  /**
   * Bring the "Play <name>" card into view: where a Challenge Me link lands (`/profile/<name>#challenge`).
   * Only somebody else's page has one (your own has nothing to challenge); returns whether it was there.
   */
  focusChallenge(): boolean {
    const card = this.#profile.querySelector<HTMLElement>(`#${CHALLENGE_ANCHOR}`);
    if (!card) return false;
    // After the screen has taken its size (the fit measures on the next frame).
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        card.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' }),
      ),
    );
    return true;
  }

  /** Nothing to show yet — a skeleton rather than an empty card. */
  pending(): void {
    this.#profile.className = 'w21-profile';
    this.#profile.replaceChildren(el('p', 'w21-sheet__lede', 'Loading…'));
    this.#fit.measure();
  }

  /** The profile could not be read. Says which, because the two differ. */
  fail(message: string): void {
    this.#profile.className = 'w21-profile';
    this.#profile.replaceChildren(el('p', 'w21-form__error', message));
    this.#fit.measure();
  }

  /** Bumped by every `show()`, so a page of games that lands after the viewer has moved on is dropped. */
  #gamesEpoch = 0;

  /**
   * "Recent games" (Blockari's `ProfileGames`): the player's SETTLED games as History's compact rows, without a row
   * link (there is no id to open, and no code to show, on somebody else's game; the opponent's name links to their
   * profile instead), 20 a page with Show more. The server decides what a visitor may see (an open challenge is simply
   * not in the list) and `publicRows` is the client's own lock on the same rule.
   */
  #gamesPanel(profile: Profile): HTMLElement {
    const load = this.callbacks.games!;
    const epoch = ++this.#gamesEpoch;
    const side = el('section', 'w21-panel w21-profile-side w21-profile-games');
    side.setAttribute('aria-label', 'Recent games');
    const head = el('div', 'w21-panel-head');
    head.append(el('h3', undefined, 'Recent games'));
    const list = el('div', 'w21-glist');
    const footer = el('div', 'w21-games-more');
    footer.hidden = true;
    const body = el('div');
    side.append(head, body);

    let count = 0;
    let next: string | null = null;
    const more = el('button', 'w21-btn w21-ghost', 'Show more') as HTMLButtonElement;
    more.type = 'button';
    const note = el('span', 'w21-t-caption');
    note.hidden = true;
    footer.append(more, note);

    const skeleton = (): HTMLElement => {
      const wrap = el('div', 'w21-glist');
      for (let i = 0; i < 3; i++) wrap.append(el('div', 'w21-skeleton w21-card'));
      return wrap;
    };
    const paint = (page: GamesPage): void => {
      for (const row of publicRows(page.games)) {
        list.append(gameRow(gameRowData(row), { k: count++ % 20, onProfile: (name) => this.callbacks.onOpenProfile?.(name) }));
      }
      next = page.next;
      footer.hidden = next === null;
    };
    const first = (): void => {
      body.replaceChildren(skeleton());
      void load(profile.username).then((r) => {
        if (epoch !== this.#gamesEpoch) return;
        if (!r.ok) {
          const retry = kitButton('Try again', 'w21-btn w21-ghost', first);
          body.replaceChildren(el('p', 'w21-t-caption', 'Could not load recent games.'), retry);
        } else if (publicRows(r.value.games).length === 0) {
          body.replaceChildren(emptyBlock('No games yet', 'Settled challenges show up here, win or lose.'));
        } else {
          body.replaceChildren(list, footer);
          paint(r.value);
        }
        this.#fit.measure();
      });
    };
    more.addEventListener('click', () => {
      if (next === null || more.disabled) return;
      more.disabled = true;
      more.textContent = 'Loading\u2026';
      note.hidden = true;
      void load(profile.username, next).then((r) => {
        if (epoch !== this.#gamesEpoch) return;
        more.disabled = false;
        more.textContent = 'Show more';
        if (r.ok) paint(r.value);
        else {
          note.textContent = 'Could not load more. Try again.';
          note.hidden = false;
        }
        this.#fit.measure();
      });
    });
    first();
    return side;
  }

  /** The tile grid, in Blockari's order; what each tile says is in profile-stats.ts. */
  #statTiles(profile: Profile, mine: boolean): HTMLElement {
    const stats = el('div', 'w21-stats');
    const openScores = mine ? this.callbacks.onBestScores : undefined;
    const loadStreaks = this.callbacks.streaks;
    for (const t of profileTiles(profile)) {
      if (t.key === 'high') stats.append(bestScoreTile(profile, openScores));
      // Best streak: a button on anyone's page when there is a sheet behind it (a card streak, or two wins).
      else if (t.key === 'streak' && t.more && loadStreaks) {
        stats.append(buttonTile(t.label, t.value, t.more, false, () => openStreakSheet(profile.username, loadStreaks)));
      } else stats.append(stat(t.label, t.value, t.unit, t.hi));
    }
    return stats;
  }

  /**
   * The viewer's own money card and stat tiles again from a newer read, in place: the Stats/Rewards tab stays where it
   * is, the scroll stays, and only a tile (or the card) whose figures changed is swapped, so nothing blanks and no
   * entrance replays. A failed finance read (`null`) leaves the card as it was.
   */
  refreshStats(profile: Profile, finance: Finance | null): void {
    if (!this.showingOwn) return;
    const top = this.root.scrollTop;
    // Its first game may have just ended: the Recent panel says "No challenges yet" until the tiles say otherwise.
    this.#hasGames = profile.gamesPlayed > 0 || profile.challengesPlayed > 0;
    this.#syncRecent();
    const grid = this.#statsBody.querySelector<HTMLElement>('.w21-stats');
    if (grid) patchRows(grid, this.#statTiles(profile, true));
    const card = this.#statsBody.querySelector<HTMLElement>('.w21-fin');
    if (card && finance) {
      const next = finCard(finance);
      // The foot is not part of the figures: set aside while they are compared, and put on the card that stays.
      this.#faucetFoot?.remove();
      if (card.outerHTML !== next.outerHTML) card.replaceWith(next);
      this.#withFaucet(card.isConnected ? card : next);
    }
    this.#fit.measure();
    this.root.scrollTop = top;
  }

  show(profile: Profile, mine: boolean, finance?: Finance | null): void {
    this.root.dataset.mine = String(mine);
    this.#recentSide = null;
    // Another player's page is one centred column; your own has the recent
    // list beside it.
    this.#profile.className = mine ? 'w21-profile' : 'w21-profile w21-solo';
    const inventory = mine && CHESTS_ENABLED ? chestStore.getState().inventory : null;
    const frame = mine && inventory ? inventory.frame : (profile.frame ?? null);
    const title = mine && inventory ? inventory.title : (profile.title ?? null);

    /* -- the hero: badge, name, title, rank line, XP bar --------------------- */
    const hero = el('section', `w21-profile-hero ${rankClass(profile.rank.index)}${frameClassOf(frame)}`);
    hero.setAttribute('aria-label', `${profile.username}'s profile`);

    const id = el('div', 'w21-profile-id');
    const slot = el('span', 'w21-hero-badge');
    slot.append(rankBadgeCard(profile.rank, 72));
    const who = el('div');
    who.style.minWidth = '0';
    who.append(el('div', 'w21-t-label', mine ? 'Your profile' : 'Player'), el('h2', undefined, profile.username));
    const titleLine = profileTitle(title);
    if (titleLine) who.append(titleLine);
    const rankLine = el('div', 'w21-profile-rank');
    rankLine.append(
      el('span', 'w21-rank-name', `${profile.rank.name} ${tierNumeral(profile.rank.tier)}`),
      kitXpChip(profile.xpLevel, profile.rank),
    );
    who.append(rankLine);
    id.append(slot, who);
    hero.append(id, xpBar(profile, profile.rank));

    /* -- the 2x boost while it runs, and the way to the guide ---------------- */
    const guide = el('div', 'w21-profile-guide');
    if (mine && inventory && inventory.boostGames > 0) {
      const chip = el('a', 'w21-chip w21-boost-chip') as HTMLAnchorElement;
      chip.href = '#';
      chip.title = 'Your 2× XP bonus: it switches on by itself and uses one game each time a challenge game pays XP';
      chip.addEventListener('click', (e) => { e.preventDefault(); this.callbacks.onGuide?.(); });
      chip.append(
        el('b', undefined, `${inventory.boostMultiplier}×`),
        el('small', undefined, 'XP'),
        el('span', 'w21-boost-left', `${inventory.boostGames} ${inventory.boostGames === 1 ? 'game' : 'games'} left`),
      );
      guide.append(chip);
    }
    const hiw = el('a', 'w21-hiw-link', 'How it works ') as HTMLAnchorElement;
    hiw.href = '#guide';
    hiw.insertAdjacentHTML('beforeend', ICON_ARROW_RIGHT);
    hiw.addEventListener('click', (e) => { e.preventDefault(); this.callbacks.onGuide?.(); });
    if (this.callbacks.onGuide) guide.append(hiw);
    if (guide.childElementCount) hero.append(guide);

    // The next level is a new rank only at tier V.
    const nextRank = rankFor(profile.xpLevel + 1);
    if (nextRank.index !== profile.rank.index) {
      const caption = el('p', 'w21-t-caption');
      caption.style.marginTop = '8px';
      const name = el('b', undefined, nextRank.name);
      name.style.color = 'var(--text)';
      caption.append(document.createTextNode('The next level is a new rank: '), name, document.createTextNode('.'));
      hero.append(caption);
    }

    /* -- Stats | Rewards (your own page, chests on) -------------------------- */
    const tabbed = mine && this.callbacks.rewards !== undefined;
    this.#rewardsHost = el('div', 'w21-rewards');
    this.#rewardsHost.hidden = true;
    this.#statsBody = el('div', 'w21-profile-body');
    if (tabbed) {
      this.#tabSeg = segmented('Profile section', [['stats', 'Stats'], ['rewards', 'Rewards']], 'stats', (v) => this.#selectTab(v));
      const tabs = el('div', 'w21-profile-tabs');
      tabs.append(this.#tabSeg.root);
      hero.append(tabs);
    } else {
      this.#tabSeg = null;
    }

    /* -- the money (yours: private and exact; theirs: the public ledger) ----- */
    if (mine) this.#statsBody.append(this.#withFaucet(finance ? finCard(finance) : finSkeleton()));
    else this.#statsBody.append(ledgerCard(profile));
    // "Free tokens" is MINE alone: another player's page never builds it and never asks the faucet anything.
    this.#faucet?.setAccount(mine ? profile.username : null, profile.isGuest);

    /* -- the tiles, in Blockari's order (profile-stats.ts holds what each one says) -------------------- */
    const stats = this.#statTiles(profile, mine);
    this.#statsBody.append(stats);
    hero.append(this.#statsBody, this.#rewardsHost);

    // Guests are told the truth about what happens to this page, and given the
    // one action that changes it. A rank nobody warned you was temporary is a
    // rank you lose without ever being asked. Own page only.
    if (mine && profile.isGuest) {
      const wrap = el('div', 'w21-profile-note');
      wrap.append(guestNote(() => this.callbacks.onCreateAccount()));
      hero.append(wrap);
    }

    // Somebody else's page ends with the one thing you can do about it.
    if (!mine) {
      const cta = el('div', 'w21-profile-cta');
      // `/profile/<name>#challenge` — a Challenge Me card's QR and link — scrolls here (`focusChallenge`).
      cta.id = CHALLENGE_ANCHOR;
      const copy = el('div');
      const lead = el('b');
      lead.append(document.createTextNode('Play '), el('span', 'w21-name-case', profile.username));
      copy.append(
        lead,
        el('span', undefined, 'Create a challenge and send them the code, or take one from the queue.'),
      );
      const row = el('div', 'w21-row');
      const challenge = el('a', 'w21-btn w21-warm') as HTMLAnchorElement;
      challenge.href = '#create';
      challenge.append(document.createTextNode('Challenge '), el('span', 'w21-name-case', profile.username));
      challenge.addEventListener('click', (event) => {
        event.preventDefault();
        this.callbacks.onChallenge();
      });
      const take = el('a', 'w21-btn', 'Take a challenge') as HTMLAnchorElement;
      take.href = '#take';
      take.addEventListener('click', (event) => {
        event.preventDefault();
        this.callbacks.onTake();
      });
      row.append(challenge, take);
      cta.append(copy, row);
      hero.append(cta);
    }

    this.#profile.replaceChildren(hero);

    /* -- somebody else's page: their settled games, newest first, a page at a time ------------------- */
    if (!mine && this.callbacks.games) this.#profile.append(this.#gamesPanel(profile));

    /* -- my own page: the recent list beside it ---------------------------- */
    if (mine) {
      const side = el('section', 'w21-panel w21-profile-side');
      const head = el('div', 'w21-panel-head');
      const all = el('a', 'w21-btn w21-ghost w21-sm', 'All') as HTMLAnchorElement;
      all.href = '#history';
      all.addEventListener('click', (event) => {
        event.preventDefault();
        this.callbacks.onHistory();
      });
      head.append(el('h3', undefined, 'Recent challenges'), all);
      side.append(head);
      // The first six. A player with no games has no recent games: the panel
      // was listing fixtures under a grid reading 0 played, a card arguing
      // with the tiles above it.
      const rows = profile.gamesPlayed > 0 || profile.challengesPlayed > 0 ? this.#recent.slice(0, 6) : [];
      this.#recentSide = side;
      this.#hasGames = profile.gamesPlayed > 0 || profile.challengesPlayed > 0;
      this.#recentEmpty = null;
      if (rows.length > 0) {
        side.append(matchRows(rows, () => this.callbacks.onHistory(), { code: false }));
      } else {
        const empty = emptyBlock('No challenges yet', 'Every challenge game pays XP; a win pays more.');
        // The two ways to change that, as Blockari's Empty carries them.
        const actions = el('div', 'w21-row');
        const { onCreate, onTake } = this.callbacks.bar ?? {};
        if (onCreate) actions.append(kitButton('Create one', 'w21-btn w21-warm', onCreate));
        if (onTake) actions.append(kitButton('Take one', 'w21-btn', onTake));
        if (actions.childElementCount) empty.append(actions);
        this.#recentEmpty = empty;
        side.append(empty);
      }
      this.#profile.append(side);
      if (this.callbacks.onSettings) {
        const settings = el('a', 'w21-btn w21-settings-link') as HTMLAnchorElement;
        settings.href = '#settings';
        settings.innerHTML = ICON_GEAR;
        settings.append(document.createTextNode(' Settings'));
        settings.addEventListener('click', (event) => {
          event.preventDefault();
          this.callbacks.onSettings?.();
        });
        this.#profile.append(settings);
      }
    }

    this.#fit.measure();
  }
}

const ICON_ARROW_RIGHT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>';
const ICON_GEAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"></path></svg>';

/** The frame class for a hero's rim: ` w21-framed w21-f-<id>`, or nothing without one. */
function frameClassOf(id: string | null | undefined): string {
  const c = cosmeticOf(id);
  return c && c.kind === 'frame' ? ` w21-framed w21-f-${c.id}` : '';
}

const RARITY_TITLE_COLOR: Record<string, string> = { common: '#bfc8e2', rare: '#4fb3ff', epic: '#d873ff', legendary: '#ffc53d' };

/** The equipped title, under the name (Blockari's `ProfileTitle`). */
function profileTitle(id: string | null | undefined): HTMLElement | null {
  const c = cosmeticOf(id);
  if (!c || c.kind !== 'title') return null;
  const line = el('div', 'w21-profile-title', c.name);
  line.style.setProperty('--tc', RARITY_TITLE_COLOR[c.rarity] ?? '#bfc8e2');
  return line;
}

/** A segmented control in Blockari's markup (`.seg` > thumb + radios), keys and all. */
interface SegmentedControl {
  readonly root: HTMLElement;
  set(value: string): void;
}
function segmented(label: string, options: ReadonlyArray<readonly [string, string]>, value: string, onChange: (v: 'stats' | 'rewards') => void): SegmentedControl {
  const root = el('div', 'w21-seg');
  root.setAttribute('role', 'radiogroup');
  root.setAttribute('aria-label', label);
  const thumb = el('span', 'w21-seg-thumb');
  thumb.setAttribute('aria-hidden', 'true');
  root.append(thumb);
  const items = options.map(([v, text]) => {
    const b = el('button', 'w21-seg-item', text) as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.addEventListener('click', () => onChange(v as 'stats' | 'rewards'));
    b.addEventListener('keydown', (e) => {
      const n = options.length;
      const at = options.findIndex(([x]) => x === current);
      const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (at + 1) % n : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (at - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
      if (next < 0) return;
      e.preventDefault();
      onChange(options[next]![0] as 'stats' | 'rewards');
      (root.querySelectorAll<HTMLElement>('.w21-seg-item')[next])?.focus();
    });
    root.append(b);
    return b;
  });
  let current = value;
  const set = (v: string): void => {
    current = v;
    const idx = Math.max(0, options.findIndex(([x]) => x === v));
    root.style.setProperty('--n', String(options.length));
    root.style.setProperty('--i', String(idx));
    items.forEach((b, i) => { b.setAttribute('aria-checked', String(i === idx)); b.tabIndex = i === idx ? 0 : -1; });
  };
  set(value);
  return { root, set };
}

/**
 * The value of a stat tile: `--n` is its length, which the tile's CSS reads to shrink the figure to fit
 * instead of cutting it off (Blockari's `Stat`).
 */
function statValue(text: string): HTMLElement {
  const b = el('b', undefined, text);
  b.style.setProperty('--n', String(text.length));
  return b;
}

/** One tile of the stats grid: a label over a display number, and a line under it (the unit, or "NN% win rate"). */
function stat(label: string, value: string, unit?: string, hi = false): HTMLElement {
  const tile = el('div', hi ? 'w21-stat w21-hi' : 'w21-stat');
  tile.append(el('span', 'w21-t-label', label), statValue(value));
  if (unit) tile.append(el('small', 'w21-stat-sub', unit));
  return tile;
}

/**
 * The account chip in the kit's markup: `small` XP · `.lv` LV · `b` level.
 *
 * Says XP out loud, every time, because the match chip beside it says
 * "Match LV" and the two numbers are unrelated.
 */
function kitXpChip(level: number, rank: RankView): HTMLElement {
  const chip = el('span', `w21-chip w21-xp ${rankClass(rank.index)}`);
  chip.title = `XP level ${level} · ${rank.name} ${tierNumeral(rank.tier)}`;
  const b = el('b');
  b.append(odometer(level));
  chip.append(el('small', undefined, 'XP'), el('span', 'w21-lv', 'LV'), b);
  return chip;
}

/**
 * The kit's `Odometer`: one column per digit, so a level that changes can
 * flip digit by digit. Static here — the profile's level does not change
 * while it is on screen — but the structure is Blockari's, and the flip is
 * a matter of swapping a column's child.
 */
export function odometer(value: number, min = 1): HTMLElement {
  const odo = el('span', 'w21-odo');
  odo.setAttribute('aria-label', String(value));
  for (const digit of String(Math.max(0, Math.floor(value))).padStart(min, '0')) {
    const col = el('span', 'w21-odo-col');
    col.append(el('span', undefined, digit));
    odo.append(col);
  }
  return odo;
}

/**
 * The XP bar to the next level, `size="lg"`: the track, then "n / m XP" and
 * "k to <next rank>". The bar spans this level only — from the threshold it
 * started at to the one it needs — rather than from zero; a lifetime bar
 * would be a sliver that never visibly moves.
 */
function xpBar(profile: Profile, rank: RankView): HTMLElement {
  const span = Math.max(1, profile.nextThreshold - profile.prevThreshold);
  const p = Math.min(1, Math.max(0, (profile.xp - profile.prevThreshold) / span));
  const bar = el('div', `w21-xp-bar w21-lg ${rankClass(rank.index)} w21-reveal`);
  bar.style.setProperty('--p', String(p));
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-valuemin', String(profile.prevThreshold));
  bar.setAttribute('aria-valuemax', String(profile.nextThreshold));
  bar.setAttribute('aria-valuenow', String(Math.round(profile.xp)));
  bar.setAttribute(
    'aria-label',
    `XP level ${profile.xpLevel}: ${formatScore(Math.round(profile.xp))} of ${formatScore(profile.nextThreshold)}`,
  );
  const track = el('div', 'w21-xp-track');
  track.append(el('i', 'w21-xp-fill'));
  const legend = el('div', 'w21-xp-legend');
  const nums = el('span', 'w21-xp-nums');
  nums.append(
    el('b', undefined, formatScore(Math.round(profile.xp))),
    document.createTextNode(` / ${formatScore(profile.nextThreshold)} XP`),
  );
  const next = rankFor(profile.xpLevel + 1);
  const toGo = Math.max(0, profile.nextThreshold - Math.round(profile.xp));
  legend.append(
    nums,
    el('span', 'w21-xp-next', `${formatScore(toGo)} to ${next.name} ${tierNumeral(next.tier)}`),
  );
  bar.append(track, legend);
  return bar;
}

/** A kit button that is a link in Blockari — an `a.btn` with the click wired. */
export function kitButton(label: string, cls: string, on: () => void): HTMLAnchorElement {
  const a = el('a', cls, label) as HTMLAnchorElement;
  a.href = '#';
  a.addEventListener('click', (event) => {
    event.preventDefault();
    on();
  });
  return a;
}

/** The kit's `Empty`: a heading and a line, in a panel that has nothing to list. */
function emptyBlock(title: string, body: string): HTMLElement {
  const empty = el('div', 'w21-empty');
  empty.append(el('h3', undefined, title), el('p', undefined, body));
  return empty;
}

/**
 * The $CHAIN record — Blockari's `ChainLedger`: the headline is what was WON
 * and the share of the stake it represents, a split bar of won against lost,
 * then WON / LOST / STAKED with nothing under them.
 *
 * Over complete challenges only — a challenge still waiting for a taker has
 * its fee refundable and therefore not yet at risk, so counting it would make
 * a player's record worse for creating one and better when it expired.
 */
function ledgerCard(profile: Profile): HTMLElement {
  const staked = profile.chainPnlPct !== null;
  const up = profile.chainWon >= profile.chainLost;
  const total = profile.chainWon + profile.chainLost;
  const wonShare = total > 0 ? profile.chainWon / total : 0.5;
  // 0 is "0", never signed; a minus is U+2212, not a hyphen.
  const signed = (n: number): string =>
    n === 0 ? '0' : `${n < 0 ? '−' : '+'}${formatScore(Math.abs(n))}`;

  const card = el('section', `w21-ledger ${staked ? (up ? 'w21-up' : 'w21-down') : 'w21-empty'}`);
  card.setAttribute('aria-label', '$CHAIN won and lost');

  const head = el('div', 'w21-ledger-head');
  head.append(coin());
  if (staked) {
    const pnl = el('b', 'w21-pnl');
    pnl.append(
      document.createTextNode(`${signed(profile.chainWon)} $CHAIN`),
      el('span', 'w21-sep', '·'),
      document.createTextNode(`${(profile.chainPnlPct ?? 0).toLocaleString()}%`),
    );
    head.append(pnl);
  } else {
    head.append(el('b', 'w21-pnl w21-none', 'No stakes yet'));
  }
  card.append(head);

  if (staked) {
    const bar = el('div', 'w21-ledger-bar');
    bar.style.setProperty('--won', String(wonShare));
    bar.setAttribute('role', 'img');
    bar.setAttribute('aria-label', `${profile.chainWon} won, ${profile.chainLost} lost`);
    if (profile.chainWon > 0) bar.append(el('i', 'w21-won'));
    if (profile.chainLost > 0) bar.append(el('i', 'w21-lost'));
    card.append(bar);
  }

  // The player's BALANCE, public like the rest of the chain: the holdings the server serves a visitor (spendable plus
  // the stakes in challenges not yet settled), so it never gives away a challenge the player has open.
  if (profile.balance !== undefined) {
    const balance = el('div', 'w21-ledger-balance');
    balance.dataset.testid = 'balance';
    balance.append(coin(), el('span', 'w21-t-label', 'Balance'), el('b', undefined, profile.balance.toLocaleString()), el('small', undefined, '$CHAIN'));
    card.append(balance);
  }

  const cols = el('div', 'w21-ledger-cols');
  const col = (cls: string, label: string, value: string): HTMLElement => {
    const c = el('div', cls);
    c.append(el('span', 'w21-t-label', label), el('b', undefined, value));
    return c;
  };
  cols.append(
    col(`w21-col w21-won${profile.chainWon === 0 ? ' w21-zero' : ''}`, 'Won', signed(profile.chainWon)),
    col(`w21-col w21-lost${profile.chainLost === 0 ? ' w21-zero' : ''}`, 'Lost', signed(-profile.chainLost)),
    col('w21-col', 'Staked', formatScore(profile.chainStaked)),
  );
  card.append(cols);
  return card;
}

/**
 * The player's own money — Blockari's `FinancePanel` (private, from `/api/me/finance`; the same definitions):
 * the headline is NET $CHAIN (won − lost on settled matches), mint when ahead and rose when behind, with its
 * share of what was staked; under it Available · In play · Returned, Wins · Losses · Win rate, $ Won · $ Lost
 * · $ Staked. Refunds and chest $CHAIN are not in the net (chests live on the Rewards tab).
 */
function finCard(f: Finance): HTMLElement {
  const signed = (n: number): string => (n === 0 ? '0' : `${n < 0 ? '−' : '+'}${formatScore(Math.abs(n))}`);
  const played = f.wins + f.losses > 0;
  const up = f.netChain >= 0;
  const total = f.won + f.lost;
  const card = el('section', `w21-ledger w21-fin ${played ? (up ? 'w21-up' : 'w21-down') : 'w21-empty'}`);
  card.setAttribute('aria-label', '$CHAIN profit and loss');

  const head = el('div', 'w21-ledger-head');
  head.append(coin());
  if (played) {
    const pnl = el('b', 'w21-pnl');
    pnl.append(document.createTextNode(`${signed(f.netChain)} $CHAIN`));
    if (f.roiPct !== null) pnl.append(el('span', 'w21-sep', '·'), document.createTextNode(`${f.roiPct.toLocaleString()}%`));
    head.append(pnl);
  } else {
    head.append(el('b', 'w21-pnl w21-none', 'No settled matches yet'));
  }
  card.append(head, el('div', 'w21-fin-sub w21-t-caption', 'Net profit / loss on settled challenges'));

  if (played && total > 0) {
    const bar = el('div', 'w21-ledger-bar');
    bar.style.setProperty('--won', String(f.won / total));
    bar.setAttribute('role', 'img');
    bar.setAttribute('aria-label', `${f.won} won, ${f.lost} lost`);
    if (f.won > 0) bar.append(el('i', 'w21-won'));
    if (f.lost > 0) bar.append(el('i', 'w21-lost'));
    card.append(bar);
  }

  const cols = (...items: Array<{ cls?: string; label: string; value: string; unit?: string; tone?: string; title?: string }>): HTMLElement => {
    const row = el('div', 'w21-ledger-cols');
    for (const it of items) {
      const c = el('div', `w21-col${it.cls ? ` ${it.cls}` : ''}`);
      if (it.title) c.title = it.title;
      const b = el('b', it.tone, it.value);
      if (it.unit) b.append(el('small', undefined, it.unit));
      c.append(el('span', 'w21-t-label', it.label), b);
      row.append(c);
    }
    return row;
  };
  card.append(
    cols(
      { label: 'Available', value: formatScore(f.balance), tone: 'w21-amber' },
      { label: 'In play', value: formatScore(f.inChallenges.chain), ...(f.inChallenges.count > 0 ? { unit: `${f.inChallenges.count}×` } : {}), title: 'Stake in challenges that are not decided yet' },
      { label: 'Returned', value: formatScore(f.returned.chain), title: 'Refunded by expired or cancelled challenges (not winnings)' },
    ),
    cols(
      { cls: `w21-won${f.wins === 0 ? ' w21-zero' : ''}`, label: 'Wins', value: f.wins.toLocaleString() },
      { cls: `w21-lost${f.losses === 0 ? ' w21-zero' : ''}`, label: 'Losses', value: f.losses.toLocaleString() },
      { label: 'Win rate', value: f.winPct === null ? '—' : `${f.winPct}%` },
    ),
    cols(
      { cls: `w21-won${f.won === 0 ? ' w21-zero' : ''}`, label: '$ Won', value: signed(f.won) },
      { cls: `w21-lost${f.lost === 0 ? ' w21-zero' : ''}`, label: '$ Lost', value: signed(-f.lost) },
      { label: '$ Staked', value: formatScore(f.staked) },
    ),
  );
  return card;
}

/** The money card while its figures load. */
function finSkeleton(): HTMLElement {
  const card = el('section', 'w21-ledger w21-fin w21-empty');
  card.setAttribute('aria-busy', 'true');
  card.setAttribute('aria-label', '$CHAIN');
  card.append(el('div', 'w21-skeleton w21-line w21-short'), el('div', 'w21-skeleton w21-line'));
  return card;
}

/** A stat tile that is a button: the label, the figure, and a cue with a chevron ("Top 25", "Streaks"). */
function buttonTile(label: string, value: string, more: string, hi: boolean, open: () => void): HTMLElement {
  const tile = el('button', `w21-stat${hi ? ' w21-hi' : ''} w21-tap`) as HTMLButtonElement;
  tile.type = 'button';
  tile.setAttribute('aria-haspopup', 'dialog');
  tile.append(el('span', 'w21-t-label', label), statValue(value));
  const cue = el('span', 'w21-stat-more', `${more} `);
  cue.setAttribute('aria-hidden', 'true');
  cue.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>');
  tile.append(cue);
  tile.addEventListener('click', () => open());
  return tile;
}

/** The high-score tile; on your own page a button that opens the top 25 (Blockari's `Stat` with `onClick`). */
function bestScoreTile(profile: Profile, open: (() => void) | undefined): HTMLElement {
  if (!open || profile.bestScore <= 0) return stat('High score', formatScore(profile.bestScore), undefined, true);
  return buttonTile('High score', formatScore(profile.bestScore), 'Top 25', true, open);
}
